import { chunkRng, fbm, valueNoise, clamp, lerp, hash01, hash3, mixRgb, smoothstep } from '@/core/rng';
import { BIOMES, type Biome, type BiomeKey, type Site } from './types';
import type { Region } from './region';
import { riversOf, bankCut } from './rivers';

/**
 * A chunk is a fixed square of world, identified by integer coordinates.
 *
 * Its contents are a pure function of (worldSeed, cx, cz). The same chunk built
 * on any machine, at any zoom, in any order, contains the same trees in the same
 * places. This is the property that makes the landscape continuous, and it is
 * the one rule that must never be broken: nothing in here may read the camera.
 */

export const CHUNK_SIZE = 256;        // metres across
export const LOD_RES = [65, 33, 17, 9] as const;   // vertices per side, by detail level

/**
 * The four silhouettes a plant can take. Four, because four is what reads:
 * a broad crown, a vertical spire, a low mound, a spray of blades. The render
 * side keeps one geometry per kind (`render/vegetation.ts`); this side decides
 * which kind stands where, as a pure function of position.
 */
/**
 * The silhouettes the world grows.
 *
 * The first four drew all twelve biomes for a long time, and `tools/form.ts`
 * priced what that cost: **14 of 45 biome pairs sat under 0.25** total
 * variation on silhouette mix — they wore the same clothes however differently
 * they were tinted. The last four are not decoration; each was chosen to split
 * a specific pair the probe named:
 *
 * | added | splits | was |
 * |---|---|---|
 * | `spreading` | oak woodland from riparian forest | **0.03** — both 70% `broadleaf` |
 * | `reed`      | tidal marsh from grassland and shore | 0.11, 0.09 |
 * | `rosette`   | dry scrub from coastal scrub and chaparral | 0.11, 0.12 |
 * | `cushion`   | alpine from chaparral | 0.11 |
 *
 * Re-run `tools/form.ts` after touching `KIND_MIX`; the 31% is the number.
 */
export type VegKind =
  | 'broadleaf' | 'conifer' | 'shrub' | 'tuft'
  /** A wide low crown on a short bole — an oak, not a river tree. */
  | 'spreading'
  /** A tall narrow stand of vertical blades — sedge, cattail, reedbed. */
  | 'reed'
  /** A spiky radial rosette — agave and yucca country. */
  | 'rosette'
  /** A tight low dome, hugging the ground out of the wind — alpine cushion. */
  | 'cushion'
  /** A narrow vertical flame of a crown — poplar, young cypress; river margins. */
  | 'columnar'
  /** A bare stem under a burst of fronds — the coast's own postcard. */
  | 'palm'
  /** A tree that died standing: bone-grey trunk, bare branches, no crown. */
  | 'snag'
  /** Arching fronds in the shade — the damp forest floor made visible. */
  | 'fern'
  /** A head-high flush of bloom over green — meadow, verge and alpine turf. */
  | 'flower'
  /** A weeping crown trailing toward the water it stands by. */
  | 'willow'
  /**
   * The seven kinds v0.5 adds, and the rule that chose them.
   *
   * `tools/form.ts` on the shipped world named **9 of 45 pairs under 0.25**,
   * and every one of them was between two of six SHORT open biomes — shore,
   * chaparral, grassland, coastal scrub, dry scrub, alpine, whose mean plant
   * heights were 1.7 / 1.6 / 2.1 / 2.2 / 1.4 / 1.0 m. That is the same finding
   * §13 recorded about marsh: *a silhouette only pays where the plant is tall
   * enough to see*, and marsh generating 47% reed at a median 1.6 m still
   * photographed as bare green ground.
   *
   * | added | pair it is aimed at | was |
   * |---|---|---|
   * | `succulent` | dry scrub / chaparral, coastal scrub, shore | 0.15, 0.17, 0.19 |
   * | `broom`     | chaparral / coastal scrub, shore | 0.18, 0.21 |
   * | `plume`     | grassland / shore, coastal scrub | 0.20, 0.20 |
   * | `umbel`     | coastal scrub / shore, grassland | 0.12, 0.20 |
   * | `creeper`   | shore / everything dry and open | 0.12–0.21 |
   * | `krummholz` | alpine / chaparral | 0.24 |
   * | `candle`    | none — it is the rare one, and it is meant to be found |
   *
   * (Every figure in this comment reproduces: `npx tsx tools/form.ts` at
   * `git show HEAD` for the "was" column and the six means, and at the working
   * tree for the table below.)
   *
   * So these were not picked to move the probe. Five of the seven stand ABOVE
   * the biome that grows them, because a silhouette a metre high in a
   * metre-high biome is a number in a table and nothing on the screen —
   *
   * **and the metres that sentence is about are DRAWN metres, not `scaleY`.**
   * The two differ by the kind's build height (see `Instance.scaleY`) and it
   * is not a rounding error: `umbel` was justified at a nominal 1.41 m in a
   * biome averaging 2.3 m nominal, and drew at 1.22 m against a 1.23 m biome
   * median — 0.99×, the one kind aimed at the tightest pair on the island and
   * the one kind that did not clear its own country. Its height law was raised
   * ×1.26 once that was measurable. `tools/form.ts`'s §"as drawn" table, on
   * the 900-chunk walk, now reports the seven against the biome each was AIMED
   * at above (the tool's own last column instead names the biome each is most
   * numerous in, which for two of them is a different one — say which you mean):
   *
   * | kind | drawn | the biome it is aimed at, drawn | |
   * |---|---:|---:|---:|
   * | `candle`    | 3.94 m | chaparral 1.15 m | 3.4× |
   * | `succulent` | 2.32 m | dry scrub 1.04 m | 2.2× |
   * | `umbel`     | 1.54 m | coastal scrub 1.28 m | 1.2× |
   * | `plume`     | 1.44 m | grassland 1.17 m | 1.2× |
   * | `broom`     | 1.42 m | chaparral 1.15 m | 1.2× |
   * | `krummholz` | 0.52 m | alpine 0.54 m | 0.96× |
   * | `creeper`   | 0.10 m | shore 1.03 m | 0.10× |
   *
   * The two that differ: most `plume` actually stands in oak woodland, where
   * it draws 0.56× a much taller median, and most `krummholz` in chaparral, at
   * 0.45×. Both are doing what their tilts say — `plume` follows grass and
   * `krummholz` follows altitude, and neither tilt reads the biome table —
   * but a ratio quoted without the biome it is against says nothing.
   *
   * The last two rows are deliberate and always were: `creeper` and
   * `krummholz` are flat and wide and separate by GROUND texture and colour
   * instead, which is the other half of what §13 says short country needs.
   * What the table also shows is how far the two columns can diverge —
   * `plume` and `broom` clear their biomes by a fifth, not by the half their
   * nominal heights suggest, and that margin is the honest one.
   */
  /** A fluted column with a few raised arms. Dry country's one tall thing. */
  | 'succulent'
  /** Blades under a spray of pale seed plumes, head-high — bunchgrass. */
  | 'plume'
  /** A sheaf of stiff, near-leafless wands standing straight up — chaparral. */
  | 'broom'
  /** A prostrate radial mat of trailing runners — the strand above the tide. */
  | 'creeper'
  /** A bare stalk under one flat pale head — the verge's umbellifer. */
  | 'umbel'
  /**
   * A conifer beaten flat by the wind, crawling downwind — treeline.
   *
   * It is aimed at the alpine / chaparral pair, and `tools/form.ts --census`
   * shows it is not an alpine plant: **24,045 island-wide, of which chaparral
   * holds 12,619, montane conifer 5,908 and alpine only 2,665** — 4.7× more in
   * chaparral than in the biome it was written for. That is `standCharacter`
   * doing exactly what its comment says (the `+ alpine · 0.07` term is
   * additive, so krummholz follows ALTITUDE and ignores the biome table), and
   * the island's high chaparral is a great deal more ground than its alpine:
   * 412 chunks of 13,470 are alpine-centred, 3.1%.
   *
   * Keep the two numbers apart. 2,665 is how many stand on alpine ground;
   * 24,045 is how many exist. "Scarce" is a claim about the second, and it is
   * not true of this kind — one plant in eighty-three on the island is a
   * krummholz mat.
   */
  | 'krummholz'
  /**
   * A rosette that has bolted: one pale mast of bloom, and then the plant dies.
   * It is the one kind in the table with no pair to split — it is here because
   * a thing you can only find by going to look at it is worth more to this
   * game than a thing you are issued.
   *
   * **What it actually is, from `npx tsx tools/form.ts --census`** — an
   * exhaustive walk of all 13,470 land chunks at LOD 0, which is the only run
   * in this repository entitled to the words "island-wide":
   *
   *     candle   5,285 standing   0.27% of the island's plants
   *              chaparral 3,944, dry scrub 1,025, coastal scrub 119,
   *              grassland 80        median drawn height 3.69 m
   *
   * Three things in that read differently from how this kind was first
   * described, and all three are worth keeping in front of the next hand:
   *
   * - **5,285, not 269.** The first figure written here was a count off a
   *   sampled walk quoted as a population. `--census` exists so the mistake
   *   has a cure rather than a warning; the stride-3 walk finds 333 of these,
   *   and a ninth of the island is a ninth of the island.
   * - **It is a chaparral plant.** Its weights are 0.03 in dry scrub and 0.01
   *   in chaparral — two biomes, not three — but chaparral is the island's
   *   largest by a wide margin, so 75% of every candle standing is in
   *   chaparral and the design line about dry country describes a quarter of
   *   them. That is generation working correctly and prose describing it
   *   wrongly.
   * - **"The tallest thing for a kilometre" is false.** It draws at 3.69 m
   *   over ground whose median plant draws 1.15 m, which is the real and
   *   sufficient claim; but chaparral also fields broadleaf, conifer,
   *   columnar, willow and snags, every one of them taller. It is a mast over
   *   the scrub, not a lone spire on a plain.
   *
   * Rare it genuinely is: only `creeper` (846), `palm` (1,533) and `succulent`
   * (3,624) stand in smaller numbers, out of twenty-one kinds.
   */
  | 'candle';

export interface Instance {
  x: number; y: number; z: number;
  /** Width — the geometry is modelled about the plant's base and scaled by this. */
  scaleXZ: number;
  /**
   * Height — as a SCALE on the kind's own build, not as metres on the screen.
   *
   * The two were treated as the same number for a long time and they are not.
   * `render/vegetation.ts` normalises every near variant of a kind to that
   * kind's far build (`matchHeight`, which is law 8 in the render tier) and
   * normalises nothing across kinds, so the builds top out where their shapes
   * put them: `columnar` at 1.0700, `broadleaf` at 0.9800, `umbel` at 0.8662,
   * `krummholz` at 0.6574, `creeper` at 0.4218, `cushion` at 0.2957.
   * `render/chunkView.ts` composes `scale(scaleXZ, scaleY, scaleXZ)` against
   * the geometry as built, so
   *
   *     drawn height = scaleY × the kind's build height
   *
   * and a `creeper` at `scaleY` 0.23 stands 0.10 m out of the ground.
   *
   * This matters wherever a height is *argued* about rather than merely
   * stored, and the size laws in `buildVegetation` are full of such arguments.
   * `tools/form.ts`'s §"as drawn" table reports both columns side by side and
   * is the only place either may be quoted from; the numbers above are its
   * `build h` column. It exists because a v0.5 height law was justified in
   * this number and turned out to fail in the other one.
   *
   * The thresholds that read `scaleY` directly — `keepFraction`'s tables,
   * `wear.ts`'s 18 m, `namePlant`'s 6 m and 18 m — are all reading the nominal
   * scale, consistently, and none of them is a claim about what the eye sees.
   */
  scaleY: number;
  rot: number;
  colour: [number, number, number];
  /**
   * HOW BURNT THIS PLANT IS, 0..1, and how long ago — written by
   * `world/overlay.ts` when a scar covers it, absent everywhere else.
   *
   * Generation never sets these: a burn is history, and `CURRENT WORLD =
   * BASELINE + SPARSE HISTORY` (law 2) means the baseline cannot know about
   * one. `render/chunkView.ts` carries them into the `aBurn` instanced
   * attribute, where the vegetation shader eats the foliage and weathers the
   * wood. Absent is the zero-cost path in every reader.
   */
  char?: number;
  charAge?: number;
  kind: VegKind;
  /**
   * Which of the kind's near-tier builds this plant wears, hashed from its
   * own position — so it is the same build at every rung and in every worker.
   * Purely a matter of *drawing*: the far tier collapses every variant to one
   * shape, which changes nothing the world knows about the plant.
   */
  variant: number;
}

/**
 * Seeded near-tier variants per kind. World generation picks an instance's
 * variant with a hash of its position; `render/vegetation.ts` builds exactly
 * this many near geometries per kind, so the two cannot disagree. One means
 * the kind has a single (still richer) near build.
 */
/**
 * Widened where it is CHEAP, and the measurement that says which those are.
 *
 * A variant is not free: `chunkView.ts` makes one InstancedMesh per (kind,
 * variant) standing in a chunk, and at LOD 0 each one is submitted twice
 * because LOD 0 is the only rung that casts a shadow. `tools/foliage.ts`
 * section 5 prices it per kind — at `git show HEAD:src`, on 269 vegetated
 * chunks, the baseline spent 21.9 groups per chunk, and the spend was
 * **shrub 3.9 and tuft 3.3 against broadleaf 2.3**: the two kinds with the
 * FEWEST variants were the two costing the most groups, because they stand in
 * every chunk and the trees do not. So the trees are where variants can be
 * bought. (Same tool, same 269 chunks, on the tree as it stands: 24.2 groups
 * per chunk, shrub 3.3, tuft 2.7, broadleaf 2.4.)
 *
 * Hence: broadleaf/conifer/spreading up by one or two (they are also three of
 * the four §4 at that revision ranked weakest — willow 0.0372, broadleaf
 * 0.0389, conifer 0.0402), shrub and tuft held exactly where they were, and
 * the seven new kinds at three apiece, which is enough for a stand to look
 * dealt rather than stamped without putting a third mesh in every chunk that
 * grows one.
 *
 * **`willow` was raised to 7 and put back to 5**, and the reason is worth
 * keeping: it was the weakest-measuring kind in the kit, so more builds looked
 * like the obvious answer, and re-running the probe showed it jumping
 * straight into the top six group-spenders at 1.8 groups per chunk for a kind
 * that is 7% of one biome and 6% of another. The variance it needed came from
 * widening `willowNear`'s own structural spans instead — same fix, no calls.
 * (That one is a measurement of a state that is deliberately NOT in the tree.
 * To re-derive it, set `willow: 7` here and run §5: willow enters the top six
 * at 1.8 and the mean goes 24.2 → 24.5. At 5, §4 now scores it 0.0547 and the
 * weakest three are plume 0.0267, candle 0.0301, krummholz 0.0395 — the new
 * kinds at three builds each, which is the trade this table made knowingly.)
 * **More variants is the expensive way to buy variety and it should be the
 * last one tried**, after per-instance colour (free), per-instance proportion
 * (free) and wider structure inside the builders (free).
 */
export const VEG_VARIANTS: Record<VegKind, number> = {
  broadleaf: 8, conifer: 8, shrub: 5, tuft: 4, spreading: 7, reed: 3,
  rosette: 3, cushion: 3, columnar: 4, palm: 4, snag: 6, fern: 4,
  flower: 4, willow: 5,
  succulent: 3, plume: 3, broom: 3, creeper: 3, umbel: 3, krummholz: 3,
  candle: 3,
};

export interface ChunkData {
  cx: number; cz: number; lod: number;
  res: number;
  /** Heights, row-major, res × res. */
  height: Float32Array;
  /** Vertex colours, res × res × 3, already 0..1. */
  colour: Float32Array;
  /**
   * What the ground is *made of*, res × res × 4: normalized weights over the
   * four texture families the terrain shader draws — grass, broken soil,
   * loose sand-and-gravel, bedded rock. Vertex colour says what tint the
   * ground has; this says what grain it wears, which is the difference
   * between paint and a surface.
   */
  material: Float32Array;
  /**
   * How WET the ground is, res × res, 0..1 — the world's own moisture read
   * (baseline rain and accumulated drainage, gated by slope and stone), which
   * `render/chunkView.ts` draws as a damp surface: darker, cooler, a sheen
   * toward a low sun, denser mottle. Nothing here is a simulation of water;
   * it is the same `rain` and `flow` the palette already tints by, carried to
   * the shader as a channel of its own so "wet country" can look wet before
   * the card says so. Optional for the same reason `apron` is.
   */
  wet?: Float32Array;
  /**
   * Heights one step PAST each edge — north is the row at j = −1, south at
   * j = res, west the column at i = −1, east at i = res, each res long.
   * They exist so border normals and border slopes can be true central
   * differences: with one-sided differences two chunks disagree about the
   * derivative at the very positions they share, and that disagreement is a
   * visible lighting crease along every chunk boundary. The field is pure,
   * so both sides of a same-LOD seam compute identical values from these.
   * Optional because the transforms' tests build ChunkData by hand.
   */
  apron?: { n: Float32Array; s: Float32Array; w: Float32Array; e: Float32Array };
  vegetation: Instance[];
}

export const chunkKey = (cx: number, cz: number, lod: number) => `${cx},${cz},${lod}`;

/**
 * The finest the surface is ever drawn at: LOD 0's vertex spacing, `CHUNK_SIZE
 * / (LOD_RES[0] - 1)`. Anything with a wavelength under twice this cannot be
 * represented by the mesh at any rung — it is sampled, not drawn.
 */
const LATTICE = CHUNK_SIZE / (LOD_RES[0] - 1);          // 4 m

/**
 * One fbm group, band-limited to the octaves the mesh can carry.
 *
 * `fbm` divides by the sum of all its amplitudes, so `fbm(...) - 0.5` is
 * identically `Σ aᵢ(nᵢ − 0.5) / N`. Truncating THAT sum is a true low-pass: the
 * octaves that survive are bit-identical to the full sum's, the mean is
 * preserved, and the only difference is the energy that was dropped. Truncating
 * the uncentred sum instead — `fbm(x, y, keep, s)` — renormalises by the
 * truncated norm and rescales the terms that were kept, which is a different
 * surface rather than a filtered one, and it also shifts the mean by
 * `−0.5·(1 − keptNorm/N)·amp`: up to **−8.2 m** on the 2.6 band. That mistake
 * made an early draft of this sink the coarse ground by metres.
 */
function band(region: Region, u: number, v: number, freq: number,
              octaves: number, salt: number, amp: number): number {
  let a = 1, norm = 0;
  for (let i = 0; i < octaves; i++) { norm += a; a *= 0.5; }
  let sum = 0, f = freq;
  a = 1;
  for (let i = 0; i < octaves; i++) {
    // wavelength of this octave, in metres of ground
    if (region.cell / f < 2 * LATTICE) break;
    sum += a * (valueNoise(u * f, v * f, region.seed + salt + i * 17) - 0.5);
    a *= 0.5; f *= 2.02;
  }
  return (sum / norm) * amp;
}

/**
 * Relief the region grid is too coarse to hold. Pure function of world position.
 *
 * **Band-limited to the lattice it is drawn on**, and that is the whole of the
 * change: the four groups used to run every octave they had, which reaches a
 * 2.45 m wavelength, while the finest mesh that ever draws them has 4 m
 * vertices. Everything under 8 m was therefore being *sampled* rather than
 * drawn, at every rung including LOD 0 — and undersampled noise on a regular
 * lattice does not read as roughness, it reads as **moiré**: the network of
 * thin dark curves across every hillside that `ROADMAP.md` §13 recorded as
 * "dashed lines" and misattributed to LOD-2 tree instances.
 *
 * It is not the trees, and `tools/lines.ts` records how that was settled: with
 * the trails, the far canopy, every chunk's vegetation, the litter, the rocks
 * and every other group hidden, the skirt vertices painted red (never once
 * visible), the shadow map off, the colour grain deleted from the shader and
 * the micro-relief uniform zeroed, the curves were still drawn. They are the
 * ground's own shape.
 *
 * What comes off is **±1.9 m of a ±28 m budget — 7%**, and none of it was ever
 * visible as relief: it is the part the mesh could only alias. Detail below the
 * lattice belongs to the shader, where `chunkView.ts` already draws it properly
 * as micro-relief at 6.0 / 2.2 / 0.85 m — normals, which need no vertices.
 * Geometry carries what the lattice can hold; the shader carries what it cannot.
 *
 * **`calm` is the bench's trade, and it composes with the band-limit rather
 * than competing with it.** These were two independent fixes to this one
 * function, made on two branches, and the merge that brought them together kept
 * only one — see `benchLift`. They are orthogonal and both are kept: `band`
 * decides which octaves may be *drawn at all*, `calm` decides how much of what
 * survives a deliberate landform buys out of its own way. Because `band` is a
 * true low-pass — the kept octaves are bit-identical to the full sum's — a
 * scalar on its output is exactly the trade the unfiltered version made.
 *
 * `calm` is how thoroughly a bench has claimed this ground. Where a riser is
 * cut, its face IS the relief, and scribbling 30 m hummocks across a face whose
 * whole meaning is that it holds one angle destroys the angle: measured, the
 * first two octaves carry a median 12.0° of their own gradient at the 4 m
 * sampling, against a riser trying to hold 34°. It must never be routed through
 * `detail` — that would make the suppression LOD-dependent and rebuild the
 * "everything floated past 768 m" bug inside the noise instead of the bench.
 *
 * Only two groups give way. The 289 m group (×38.0) is the landform-scale
 * relief the region grid cannot hold and the 10 m group (×2.2) is the surface
 * grain a hillside needs at walking distance; suppressing either would flatten
 * the island rather than sharpen it. The 100 m group is halved rather than
 * removed because it is the dominant slope carrier despite not being the
 * largest amplitude.
 */
export function detailHeight(region: Region, wx: number, wz: number,
                             calm = 0): number {
  const u = wx / region.cell, v = wz / region.cell;
  return band(region, u, v, 8.5, 4, 95, 9.0) * (1 - calm)
       + band(region, u, v, 2.6, 5, 97, 34.0) * (1 - 0.5 * calm)
       + band(region, u, v, 0.9, 4, 99, 38.0)
       + band(region, u, v, 26, 3, 91, 2.2);
}

/**
 * Where loose material stops, as a gradient (dy/dx).
 *
 * **This is NOT `earth.ts`'s `REPOSE = 1/3`, and the two must never be aliased
 * to one symbol.** That constant is the divine scoop's depth-to-radius cap — a
 * lens geometry figure, `atan(1/3) = 18.4°` — and it was offered in good faith
 * as "the island's angle of repose arriving through the back door". It is not
 * one. Nothing loose stands at 18°; scree stands near 34°, which is where the
 * mode this file is trying to create has to sit or it is a mode in a place no
 * real hillside has one. They share a word and nothing else. A tuning pass that
 * "unifies" them silently halves every riser on the island.
 */
const BENCH_REPOSE = Math.tan((34 * Math.PI) / 180);   // 0.6745

/** The knobs, as one object so `tools/repose.ts` can sweep the REAL function
 *  instead of a copy of it that drifts from it. Frozen, and `surfaceHeight`
 *  never passes an override — so the shipped surface reads these and only
 *  these, and law 1 is untouched. */
export interface BenchTune {
  /** Metres of ELEVATION per bench. Does **not** set the riser angle — riser
   *  height is `step` and riser plan width is `step / (REPOSE·(1−round))`, so
   *  `step` cancels out of the angle entirely. What it sets is the PLAN period,
   *  `step / grade`, and therefore whether the LOD ladder can resolve the
   *  bench at all. That makes it a Nyquist choice, not a taste one. */
  step: number;
  /** Fraction of a riser spent rounding into and out of the constant-angle
   *  face. Weak lever; the sweep found the 33–36° bin flat across 0.20–0.40. */
  round: number;
  /** Bench fade-in on the coarse grade. Below `onLo` the ground is a floor, a
   *  tread or a floodplain and must stay one: the wider window that reaches
   *  down to 2.9° was measured to drag the median slope from 14.1° to 12.8°
   *  and to eat the 9–12° population, which is terracing a floodplain. */
  onLo: number; onHi: number;
  /** Benched country here, ramp country there. Must stay NEAR-BINARY: `on` is
   *  a pull fraction, so a smooth 0.5 half-pulls everything and smears both
   *  modes back into the hump. Measured twice, independently, in two separate
   *  design arms — both collapsed to a monotone histogram. */
  patchLo: number; patchHi: number; patchLambda: number;
  /** Slow phase drift, in BANDS, so two hillsides across a valley do not bench
   *  at the same elevations and let the eye close the ring around a massif.
   *  Sized against the LEAK, not by eye: a phase gradient arrives in the slope
   *  multiplied by `step·|p−1|` ≈ 20–60 m per band-per-metre, so phase is
   *  20–60× more dangerous than amplitude. At these constants the leak is
   *  0.74°, invisible. One reviewed design drove this to ~0.5 bands at 620 m,
   *  computing to 18° of smear on a 34° riser — it then needed a broken ramp
   *  to produce any breaks at all. Amplitude is the safe lever; phase is not. */
  drift: number; driftLambda: number;
  /** How much of the fine noise a fully-benched place buys out of the way of
   *  its own risers — the TRADE, not an addition. See `detailHeight`'s `calm`.
   *  At 0 the bench is drawn under full hummock and the riser angle is washed
   *  out by noise carrying a median 12.0° of its own gradient; at 1 the two
   *  finest octaves vanish wherever `on` is 1. It is the difference between a
   *  hillside that is lumpier and one that is *benched*. */
  calm: number;
}

/**
 * Chosen by `tools/repose.ts --sweep`, not by eye. Every row below was measured
 * on the same 14,222 land samples, against the same seed, with the term
 * switched off for attribution:
 *
 * | step / on-window | riser p50 | median | relief250 | coverage |
 * |---|---|---|---|---|
 * | 11, 0.10–0.22 | 22.7° | −1.4° | **−6.3 m** | 21.8% |
 * | 11, 0.22–0.40 | 29.5° | −0.3° | −3.8 m | 10.6% |
 * | **16, 0.18–0.34** | **28.1°** | **−0.8°** | **−1.4 m** | 13.6% |
 * | 22, 0.22–0.40 | 29.9° | −0.5° | −4.5 m | 10.6% |
 *
 * `step` is a NYQUIST choice and this is what settled it. The riser angle is
 * independent of `step` — it cancels — so raising it is free where it matters:
 * at 11 the bench's plan period is p50 36 m, which LOD 1 (8 m vertices) cannot
 * resolve for 40% of benches and LOD 2 cannot resolve for **96%**, making the
 * whole feature a LOD-0-only object that stops existing at the 768 m ring. That
 * is the repo's own "everything past 768 m" failure wearing a different hat.
 *
 * The on-window is what protects the island from being terraced where it should
 * not be. The wider 0.10–0.22 reaches down to 2.9° of grade — onto floors,
 * treads and floodplains — and it shows: it costs 6.3 m of the 250 m local
 * relief and 1.4° of median slope, which is a rice paddy, not a hillside.
 * 0.18–0.34 keeps `ISLAND_MEDIAN_SLOPE_DEG = 14.3` (`earth.ts:190`, a hardcoded
 * copy of this statistic feeding `MEANINGFUL_HOLD`) honest without a second
 * edit in another file's constants.
 */
export const BENCH: Readonly<BenchTune> = Object.freeze({
  step: 16, round: 0.28,
  onLo: 0.18, onHi: 0.34,
  patchLo: 0.40, patchHi: 0.60, patchLambda: 700,
  drift: 0.5, driftLambda: 1900,
  calm: 1,
});

/**
 * Integral of a trapezoidal bump: 0 at t ≤ 0, 1 at t ≥ 1, a plateau of height
 * 1/(1−σ) through the middle, quadratic fillets of width σ at both ends and
 * therefore ZERO derivative at both.
 *
 * Two properties do all the work and both are exact rather than tuned:
 *
 *   - **antisymmetric about (½, ½)**, so `∫ramp = ½` exactly and the bench
 *     conserves the fall-line drop to machine precision. This is what lets a
 *     hillside be re-cut into tread and riser without the massif losing or
 *     gaining a metre of height — `tools/relief.ts` §2's local relief must not
 *     move, and it cannot, because of this.
 *   - **ramp'(0) = ramp'(1) = 0**, so the offset is C¹ across every band
 *     boundary. A term that is merely C⁰ here tears normals along every
 *     contour; one that is discontinuous cracks the mesh.
 *
 * A near-miss version of this — `1 − σ/2` in place of `1 − σ` in the upper
 * branch — jumps by `σ/(2(1−σ))` of a step at `t = 1−σ` and again at `t = 1`.
 * At a 26 m band that is two 3.25 m vertical walls per bench, which a ±4 m
 * break stencil reads as ~39°, and it manufactures a break statistic out of
 * pure discontinuity: the arm that shipped it reported breaks rising 0.79% →
 * 3.90% and none of it was the fillet. **Verify any edit here numerically:
 * the integral must be 0.5 and `ramp(1−1e−6, σ)` must be 1.0 to six places.**
 * `test/repose.ts` asserts both.
 */
export function ramp(t: number, sigma: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  if (sigma < 1e-4) return t;
  const p = 1 / (1 - sigma);
  if (t < sigma) return (p * t * t) / (2 * sigma);
  if (t > 1 - sigma) { const v = 1 - t; return 1 - (p * v * v) / (2 * sigma); }
  return p * (t - sigma / 2);
}

/**
 * Benches: level treads separated by risers at the angle of repose, cut into
 * ground that had neither.
 *
 * **Why the island has no preferred angle, and why no amount of retuning the
 * four `detailHeight` amplitudes could give it one.** Measured at the LOD-0
 * vertex spacing over 62,538 land samples, the slope histogram is a single
 * smooth hump: 3–6° at 15.7%, decaying monotonically to vertical, with the
 * 33–36° bin at 3.1% sitting obediently between its neighbours at 3.5% and
 * 2.7%. That is the signature of noise plus diffusion, and it is structural —
 * `region.heightAt` is bicubic over 260 m cells and `detailHeight` is four fbm
 * stacks, so *everything underneath is smooth*. Switching `detailHeight` off
 * entirely leaves the same single hump. A mode cannot be summed out of smooth
 * parts; it has to be manufactured by a NONLINEARITY, and the cheapest correct
 * one is to quantise a scalar and shape the fraction.
 *
 * **The scalar is `base` — the coarse height line 172 already computed — and
 * that choice is the whole trick.** The gradient of any function of elevation
 * is `∇(f∘H) = f'(H)·∇H`, which is *exactly parallel to the fall line at every
 * point*. So the bench is downslope-aligned by construction: no direction
 * vector is ever formed, none of a finite-difference stencil's aspect error can
 * reach it, and it costs **zero** extra field samples. ROADMAP §13 asks for "a
 * downslope-aligned constant-angle facet rather than more isotropic noise";
 * this is that, obtained for two fbm calls.
 *
 * **What it honestly cannot do is steepen a hillside pointwise.** A height
 * field whose gradient is `φ(|∇h|)·∇h` exists only where `∇|∇h| ∥ ∇h`, which on
 * this island it is not. So a 15° slope does not become a 20° slope; it becomes
 * alternating level tread and repose riser, with the duty cycle solved so the
 * fall-line drop is conserved exactly. Anyone reading the median slope as a
 * health metric should be told that: the median is *supposed* to sag slightly,
 * because half the ground is now flatter than it was and the rest is at 34°.
 *
 * **NOT scaled by `detail`, and that is deliberate.** The offset reaches
 * `step/2`; deflating it per rung would leave everything seated at `detail = 1`
 * — the camera floor (`main.ts:333`), rocks (`rocks.ts:195`), river ribbons
 * (`rivers.ts:132`), falls (`falls.ts:107`) and every plant (`chunk.ts:1042`) —
 * floating or sunk by metres past the LOD-0 ring at 768 m. That is the repo's
 * own documented "everything standing on the ground floated past 768 m" bug,
 * and it is avoided by making this term byte-identical at every rung.
 * It DOES stay inside `if (detail > 0)`: the horizon mesh carries a vertex
 * every 156 m (`horizon.ts:45`, res 320 over 49,920 m) while this term's plan
 * period is tens of metres, so putting it on the `detail = 0` path would alias
 * a 45–240 m periodic function on the skyline. `farField.ts:41` already wrote
 * down why that path takes no fine relief, and this is fine relief.
 */
export function benchOn(
  region: Region, wx: number, wz: number,
  base: number, grade: number, damp: number,
  t: Readonly<BenchTune> = BENCH,
): number {
  // Above repose the term is identically zero — see `sigma` in `benchLift`.
  // This is the cost gate, not the shape gate: the shape gate is arithmetic and
  // cannot be mistuned.
  if (grade >= BENCH_REPOSE) return 0;
  return smoothstep(t.onLo, t.onHi, grade)        // not on floors or treads
       * smoothstep(2, 30, base)                   // not on the shore — see below
       * smoothstep(t.patchLo, t.patchHi,          // a place, not a texture
           fbm(wx / t.patchLambda, wz / t.patchLambda, 3, region.seed + 193))
       * damp;                                     // and never across a channel
}

export function benchLift(
  region: Region, wx: number, wz: number,
  base: number, grade: number, on: number,
  t: Readonly<BenchTune> = BENCH,
): number {
  if (on <= 0 || grade >= BENCH_REPOSE) return 0;

  // As `grade` approaches repose the fillets vanish, the riser fills the whole
  // band, `ramp` degenerates to the identity and the offset goes to EXACTLY
  // zero. Cliffs are excluded by arithmetic — there is no cutoff constant here
  // to get wrong — and the amplitude falls off linearly in (1 − grade/REPOSE).
  const sigma = Math.min(t.round, 1 - grade / BENCH_REPOSE);
  // Solve the riser plateau onto repose: plateau slope = grade·p/w = REPOSE.
  const w = Math.min(1, grade / (BENCH_REPOSE * (1 - sigma)));

  // Scaled by grade because the phase gradient is amplified by
  // (REPOSE/grade − 1) on the riser — 5.7× at 5.7° of grade — and would
  // otherwise dominate the very angle this function exists to set.
  const drift = (fbm(wx / t.driftLambda, wz / t.driftLambda, 2, region.seed + 181) - 0.5)
              * t.drift * (grade / BENCH_REPOSE);

  const u = base / t.step + drift;
  const f = u - Math.floor(u);
  const a = 0.5 * (1 - w);                    // centre the riser in the band
  return t.step * on * (ramp((f - a) / w, sigma) - f);
}

/**
 * The surface: the coarse field plus the relief it is too coarse to hold.
 *
 * `detail` is now a switch rather than a dial — **1 is the land, 0 is the
 * coarse field alone.** Every streamed rung passes 1 (see `buildChunk`, which
 * records why the old per-rung ladder was wrong and what it cost); 0 is for the
 * far field and the horizon mesh, at 156.5 m per vertex, which cannot represent
 * a 30 m wave and should not pretend to. Nothing produces a value in between
 * any more. The multiply is left in place so the function stays continuous for
 * anything that wants to cross-fade between the two.
 *
 * The fine noise is scaled by how rough the coarse ground beneath it already
 * is. A floodplain and a beach are *flat* — being flat is what makes them a
 * floodplain and a beach — so giving them forty metres of hummock both
 * contradicts the deposition that flattened them and shoves half of a gentle
 * shore under the waterline, where it clamps to a dead level sheet. Steep
 * ground keeps the full amount and wants it. Pure function of position, so
 * every LOD and every worker still reads the same surface.
 *
 * **Altitude does not enter this.** It used to: `rough` was
 * `max(smoothstep(0.05, 0.26, grade), smoothstep(6, 150, base))`, and that
 * second term forced the full forty metres of hummock onto *everything* above
 * 150 m however flat it was. `region.ts` spends real effort terracing the high
 * country — `terrace()` makes 46% of every step a level tread, precisely so a
 * hillside has somewhere to stand — and this erased all of it, because the
 * terracing only operates where `relief` is high, which is exactly where the
 * altitude term pinned roughness to 1. Two systems in two files, each correct
 * alone, cancelling.
 *
 * Measured (`tools/relief.ts`, and the region grid read directly): the coarse
 * field carries **9.1%** of the island under 3°, the drawn surface carried
 * **2.8%**, and ground flat enough to stand on with thirty metres of elbow
 * room in every direction was **1.1%**. Dropping the altitude term takes that
 * last figure to **12.3%** — and the steep end does not move at all: 40–55°
 * stays 9.1%, 55° and over stays 4.4%, the 99th percentile stays 69.1°. It
 * could not have moved, because steep ground gets its roughness from `grade`
 * anyway; the altitude term was only ever adding noise to ground that was
 * already flat, which is the one place noise is not wanted.
 *
 * The rule that replaces it is the one the paragraph above was already
 * making: **detail follows local roughness, not height above the sea.**
 * `detailHeight` exists for relief the region grid is too coarse to hold, and
 * on a genuinely flat bench the grid holds it perfectly well.
 */
export function surfaceHeight(region: Region, wx: number, wz: number, detail: number): number {
  const base = region.heightAt(wx, wz);
  let h = base;
  let smooth = base;
  if (detail > 0) {
    const D = 130;
    const gx = region.heightAt(wx + D, wz) - region.heightAt(wx - D, wz);
    const gz = region.heightAt(wx, wz + D) - region.heightAt(wx, wz - D);
    const grade = Math.hypot(gx, gz) / (2 * D);
    const rough = smoothstep(0.05, 0.26, grade);
    // Water smooths its own floor. The coarse field routed the rivers, and
    // the fine hummocks were being piled straight over their channels — the
    // drawn valley floor was not the bed the routed water lies on, and the
    // ribbons in `render/rivers.ts` were measured fully buried under it (a
    // full-red emissive flash showed nothing from directly above). Along
    // strong channels the fine noise now damps away; `flowAt` is bilinear,
    // so the damping arrives as a corridor and not as 260 m tiles.
    const damp = 1 - smoothstep(5.3, 7.4, Math.log(1 + region.flowAt(wx, wz))) * 0.85;
    // The bench goes on BEFORE the noise and is NOT multiplied by `detail` —
    // see `benchLift`. It reuses `base`, `grade` and `damp` exactly as they
    // were computed above, so it costs two fbm calls and not one extra
    // `heightAt`. `on` is spent twice: once to place the bench, once to buy
    // the fine noise out of the way of its risers, which is why it is computed
    // here rather than inside `benchLift`.
    const on = benchOn(region, wx, wz, base, grade, damp);
    h += benchLift(region, wx, wz, base, grade, on);
    smooth = h;
    h += detailHeight(region, wx, wz, on * BENCH.calm) * detail * lerp(0.14, 1, rough) * damp;
  }
  // THE CHANNEL (2026-08-19, the river morphology pass): where the shared river
  // model says a river runs, the land descends into it — bank shoulder, bank
  // slope, shelf, bed, thalweg — from the same centreline, width, depth and
  // water surface the water renderer draws. `smooth` is the surface without
  // its fine noise, so a bank is cut against the valley floor and not against
  // the hummock that happened to be there; the noise comes back over the
  // outer bank. Every LOD and every worker carve the same channel.
  h = riversOf(region).carve(wx, wz, h, smooth);
  const site = region.siteAt(wx, wz);
  return site.land ? Math.max(h, 0.8) : h;   // dry land never sinks below the sea
}

/**
 * Convexity of the coarse surface under (wx, wz): positive on ridges and
 * spurs, negative in swales and hollows, saturating around ±1 at pronounced
 * relief. Every LOD — and every worker — reads the identical value, because it
 * is sampled from the region's own height field. Pure function of position.
 *
 * The baseline is a whole region cell rather than the ±90 m it used to be. The
 * field is bicubic over 260 m cells, so a second difference taken well inside
 * one cell is measuring the interpolant's own curvature and not the land's: the
 * old span returned a few hundredths over most of the island and the tint it
 * feeds — the one that stands ridges apart from swales — effectively never
 * fired. `render/rocks.ts` had already worked around this by computing its own
 * cell-wide curvature; this is the same measurement, made once, here.
 */
export function convexity(region: Region, wx: number, wz: number): number {
  const D = 260;
  const centre = region.heightAt(wx, wz) * 4;
  const around = region.heightAt(wx + D, wz) + region.heightAt(wx - D, wz)
               + region.heightAt(wx, wz + D) + region.heightAt(wx, wz - D);
  return clamp((centre - around) / 150, -1, 1);
}

/**
 * The eight region cells around a point, in a fixed order — so borrowing a
 * neighbour's soil is a pure function of position and never of visit order.
 */
const NEIGHBOURS = [
  [1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, 1], [1, -1], [-1, -1],
] as const;

/**
 * The site whose *soil* a point may use.
 *
 * Ordinarily its own. But `land` is a nearest-cell lookup on the 260 m region
 * grid while `height` is bicubic, so the two disagree within half a cell of
 * every coast — and `tools/shore.ts` measures 14 km² of this island standing an
 * average of 30 m above the water while the mask still calls it ocean. Those
 * spurs and stacks have no biome of their own; left alone they took the ocean
 * row of the palette and rendered as bare pale seabed above the waterline.
 * They borrow the nearest cell that has a soil, which is what a real headland
 * does anyway — it is the same hillside, one cell further out.
 */
function landSite(region: Region, wx: number, wz: number, site: Site): Site {
  if (site.land) return site;
  for (let ring = 1; ring <= 2; ring++) {
    for (const [dx, dz] of NEIGHBOURS) {
      const s = region.siteAt(wx + dx * region.cell * ring, wz + dz * region.cell * ring);
      if (s.land) return { ...s, height: site.height };
    }
  }
  return site;
}

/**
 * What open water shows through itself: pale wet sand in the wash, teal across
 * the shelf, then the ramp into deep blue. Read through the translucent sea in
 * `render/water.ts`, this is what makes the shallows glow.
 */
function seabedColour(height: number): [number, number, number] {
  const depth = clamp(-height, 0, 4000);
  let c = mixRgb([112, 104, 84], [44, 82, 86], smoothstep(0.5, 9, depth));
  c = mixRgb(c, [22, 42, 58], smoothstep(8, 42, depth));
  return mixRgb(c, [11, 22, 35], clamp(depth / 400, 0, 1));
}

/**
 * The site whose soil a point reads, organically. Nearest-cell sampling paints
 * the region grid onto the land as rectangles; jittering the read position
 * dissolves cell borders into wavy, biome-scale blends. Land is guarded — a
 * shoreline vertex never borrows ocean soil — and height always comes from the
 * true position.
 *
 * **The jitter has to be interpolated noise, and for a long time it was not.**
 * It read `hash01(Math.round(wx / 150), Math.round(wz / 150), …)`, which is a
 * hash of a *block index*: constant everywhere inside one 150 m block and
 * discontinuous at its edge. That does not dissolve a rectangle, it trades it
 * for two smaller ones — the ground came out as 150 m and 40 m tiles laid over
 * the region's 260 m tiles, all three axis-aligned.
 *
 * It went unnoticed because `surfaceHeight` was dumping forty metres of
 * hummock over all high ground, and broken shading hides flat tiles. Fixing
 * that (see `surfaceHeight` above) made gentle country genuinely smooth, and
 * the tiling walked straight out of the shadows — which is the usual way a
 * latent artifact announces itself: something *else* stops covering for it.
 *
 * `fbm` is interpolated, so the offset now varies continuously and the borders
 * come out wavy, which is what the paragraph above always claimed.
 */
function soilAt(region: Region, wx: number, wz: number, trueSite: Site): Site {
  const s0 = region.seed;
  // Three scales of warp, and the new large one is what makes an ecotone.
  // The 150/40 m pair dissolves cell borders into waviness; waviness is not
  // *interdigitation* — a real biome edge sends fingers a few hundred metres
  // into its neighbour and strands islands there, which needs displacement
  // larger than the 260 m cell itself, varying over ~700 m.
  const jx = (fbm(wx / 700, wz / 700, 2, s0 + 61) - 0.5) * 520
           + (fbm(wx / 150, wz / 150, 2, s0 + 51) - 0.5) * 300
           + (fbm(wx / 40, wz / 40, 2, s0 + 53) - 0.5) * 110;
  const jz = (fbm(wx / 700, wz / 700, 2, s0 + 67) - 0.5) * 520
           + (fbm(wx / 150, wz / 150, 2, s0 + 57) - 0.5) * 300
           + (fbm(wx / 40, wz / 40, 2, s0 + 59) - 0.5) * 110;
  const s = region.siteAt(wx + jx, wz + jz);
  return s.land === trueSite.land ? s : trueSite;
}

/**
 * How far the ground palette is pulled down before it reaches the renderer.
 *
 * The scene is ACES tone mapped at 1.55 exposure, and ACES compresses hard
 * above about 0.6 of its input range: past that, two materials whose albedos
 * differ by half again barely differ on screen. Written at face value the
 * ground therefore arrived as one pale wash with no tonal range left in which
 * to show its own form — measured off the framebuffer across a 6 km view of the
 * main massif, every ground pixel in the frame fell between 160 and 184 of 255,
 * summit and valley floor alike.
 *
 * Pulling the whole palette down into the responsive part of the curve is what
 * buys that contrast back, and it is what lets sunlight model a hillside at all.
 * It is applied once, here, rather than folded into forty separate constants —
 * so the colours above stay legible *as colours*, and the one place that knows
 * about the renderer's exposure is the one line that has to change if it moves.
 *
 * 0.56 rather than the 0.62 it sat at, because the lighting puts most of it
 * back: a Lambert surface under this island's midday sun and sky returns about
 * half its albedo and the 1.55 exposure then multiplies by 2.6, so the net gain
 * from albedo to ACES input is greater than one. Measured off the framebuffer
 * over the coast forest at 320 m — the range the game is actually played at —
 * the floor of a closed canopy came back at luminance 120 of 255 at 0.62. A
 * sunlit meadow reads near that; a redwood floor does not.
 */
const GROUND_EXPOSURE = 0.56;

const exposed = (c: [number, number, number]): [number, number, number] =>
  [c[0] * GROUND_EXPOSURE, c[1] * GROUND_EXPOSURE, c[2] * GROUND_EXPOSURE];

/**
 * The colour of the ground at a point.
 *
 * Every term is a pure function of world position and the region's own fields.
 * *Nothing here may read the LOD.* It used to: the drainage tint and the fine
 * grain were both gated on a `wide` factor derived from the caller's rung, so
 * the same square metre of hillside came out a different colour depending on
 * which ring of the streamer happened to be drawing it — and every LOD boundary
 * that crossed land showed as a hard-edged rectangle up to 60/255 out of step
 * with its neighbour. Distance-dependent appearance belongs to the renderer's
 * fog and to nothing in here.
 */
/** Scratch weights for callers that only want the colour. */
const MAT_SCRATCH: MatOut = [0, 0, 0, 0, 0];

/**
 * What `groundSurface` reports about a point besides its colour: the four
 * normalized texture-family weights (grass, soil, sand, rock) the terrain
 * shader grains by, and a fifth number that is not a weight — the wetness of
 * the ground, 0..1 (see `ChunkData.wet`).
 */
export type MatOut = [number, number, number, number, number];

export function groundColour(region: Region, wx: number, wz: number, slope: number) {
  return groundSurface(region, wx, wz, slope, MAT_SCRATCH);
}

/**
 * Colour *and* material of the ground at a point. `matOut` receives the
 * normalized weights of the four texture families (grass, soil, sand, rock)
 * that `render/chunkView.ts`'s terrain shader draws per pixel — computed here
 * because they read the same fields the colour does, and the two must never
 * disagree about what a square metre of the world is made of.
 */
export function groundSurface(
  region: Region, wx: number, wz: number, slope: number,
  matOut: MatOut,
) {
  const site = region.siteAt(wx, wz);
  /**
   * Where the water ends is decided by the *height*, never by the land mask.
   *
   * This is the coastal sawtooth, and it was neither the chunk skirts nor the
   * horizon mesh. `land` is nearest-cell on a 260 m grid and `height` is
   * bicubic, so the two disagree in a band up to half a cell wide all the way
   * round the island. `tools/shore.ts` measures the damage: 1.9 km² of ground
   * that the mask calls land while the height field puts it at or below sea
   * level — and the old shore term ramped *down* from 1.2 m and never ramped
   * back up on the seaward side, so every square metre of it took full-strength
   * beach sand. Pale wedges, stepping along a 260 m staircase, over open water.
   *
   * Crossfading the two palettes across the real waterline removes the whole
   * class: the mask can no longer put sand where there is sea, nor seabed where
   * there is a headland, because it is no longer asked. Inland and offshore the
   * fade is saturated, so this costs nothing anywhere but the beach.
   */
  const WASH = 1.6;
  if (site.height <= -WASH) {
    // underwater ground is wet sand and silt, whatever stands above the tide
    matOut[0] = 0; matOut[1] = 0.15; matOut[2] = 0.7; matOut[3] = 0.15;
    matOut[4] = 1;
    return exposed(seabedColour(site.height));
  }
  let land = landColour(region, wx, wz, site, slope, matOut);
  // THE RIVER'S OWN GROUND (2026-08-19): the bed and the banks read from the
  // shared river model. Inside the water the bed is wet gravel and silt —
  // sand and stone, no grass, darker; up the bank the grass gives way to
  // exposed soil near the water's edge, darker still on a bend's cut bank,
  // gravel where a bar has been laid; and the whole channel is wet ground.
  // The water renderer's colour then sits on a bed that is already a bed.
  {
    const r = riversOf(region).at(wx, wz);
    if (r && r.inChannel) {
      const ad = Math.abs(r.d);
      if (ad <= r.halfWidth) {
        const deep = smoothstep(0, 1.2, r.localDepth);
        const gravel: [number, number, number] = [92, 88, 76];
        const silt: [number, number, number] = [64, 62, 52];
        land = mixRgb(land, mixRgb(gravel, silt, deep), 0.85);
        matOut[0] *= 0.05;
        matOut[2] = Math.max(matOut[2], 0.55 - deep * 0.2);
        matOut[3] = Math.max(matOut[3], 0.25);
        matOut[1] = Math.max(0.1, 1 - matOut[0] - matOut[2] - matOut[3]);
        matOut[4] = 1;
      } else {
        const t = clamp((ad - r.halfWidth) / Math.max(0.5, r.channelHalf - r.halfWidth), 0, 1);
        const cut = bankCut(r);
        const bankSoil: [number, number, number] = cut > 0.5 ? [78, 66, 48] : [104, 90, 64];
        const gravel: [number, number, number] = [100, 96, 82];
        const bare = (1 - t) * (1 - t);
        land = mixRgb(land, mixRgb(gravel, bankSoil, clamp(cut + t * 0.6, 0, 1)), bare * 0.75);
        matOut[0] *= 1 - bare * 0.85;
        matOut[2] = Math.max(matOut[2], bare * 0.4 * (1 - cut));
        matOut[1] = Math.max(matOut[1], bare * (0.35 + cut * 0.4));
        const sum = matOut[0] + matOut[1] + matOut[2] + matOut[3];
        matOut[0] /= sum; matOut[1] /= sum; matOut[2] /= sum; matOut[3] /= sum;
        matOut[4] = Math.max(matOut[4], 1 - t * 0.7);
      }
    }
  }
  if (site.height >= WASH) return exposed(land);
  // the wash: colour crossfades to the seabed's, and so does the material
  const t = smoothstep(-WASH, WASH, site.height);
  matOut[0] *= t;
  matOut[1] = lerp(0.15, matOut[1], t);
  matOut[2] = lerp(0.7, matOut[2], t);
  matOut[3] = lerp(0.15, matOut[3], t);
  matOut[4] = lerp(1, matOut[4], t);
  return exposed(mixRgb(seabedColour(site.height), land, t));
}

/** The dry-land palette. Called only where the height field says there is land. */
function landColour(
  region: Region, wx: number, wz: number, site: Site, slope: number,
  matOut: MatOut,
) {
  const base = landSite(region, wx, wz, site);
  const soil = soilAt(region, wx, wz, base);
  const b = BIOMES[soil.biome];

  /**
   * How high this ground stands within its own region, and the three materials
   * that height is read as: soil and grass low down, dry earth and gravel
   * through the middle, cold stone under a frost-bleached cap at the top.
   *
   * Height is the strongest cue the eye has for the *size* of a landscape, and
   * it only works if the bands read as different substances. The previous cut
   * of these bands did not: `tools/palette.ts` put the lowland at luminance 131
   * and the summit at 137 — a six-count climb over 2.5 km of mountain, on an
   * island where the *within*-band spread was twenty. Every band was a mid-tone
   * grey mixed into another mid-tone grey, and a mid-tone mixed into a mid-tone
   * is a mid-tone; the summit was the brightest thing in the frame because
   * `frost` was fired from 0.76 of full height and brightened everything above
   * the treeline.
   *
   * So the bands now go somewhere. `montane` is dry earth and gravel, a little
   * warmer and a little darker than the soil below it. `stone` is dark cold
   * blue-grey and it is *strong* — real granite in daylight returns about a
   * fifth of what a sunlit meadow does, and a mountain that is not darker than
   * the valley it stands over is not a mountain. `frost` is the only brightener
   * in this whole function and it is confined to the last four hundred metres,
   * where it reads as frost precisely because the rock beneath it is dark.
   */
  const rel = clamp(site.height / (region.maxHeight * region.heightScale), 0, 1);
  const montane = smoothstep(0.22, 0.46, rel);
  const stone = smoothstep(0.44, 0.70, rel);
  const frost = smoothstep(0.79, 0.96, rel);

  // The mottle between a biome's two ground colours. This was
  // `hash01(Math.round(wx / 40), …)` — a hash of a block index, the same bug
  // class `soilAt` documents above: constant inside each 40 m square, hard
  // step at its edge, and the ground wore it as a 40 m camouflage quilt over
  // the whole island. fbm varies continuously; the ×1.8 stretch matches its
  // measured sd (0.16) back to the uniform hash's 0.29 so the mottle keeps
  // its old amplitude, clamped only in the ~8% tails.
  let c = mixRgb(b.colour[0], b.colour[1],
                 clamp((fbm(wx / 40, wz / 40, 2, region.seed + 71) - 0.5) * 1.8 + 0.5, 0, 1));
  // a common cast, so neighbouring biomes share a world rather than a swatch
  // book. It used to be 0.18 toward a mid olive, which pulled the dark forests
  // up and the pale deserts down until half the table met in the middle.
  c = mixRgb(c, [52, 58, 38], 0.10);
  // Wet country is *dark* country. This term used to run toward [74,108,62] —
  // brighter than a coast forest's own soil — so the wettest, deepest-canopied
  // ground on the island was the ground this function lightened most, and
  // redwood photographed a single count away from grassland.
  c = mixRgb(c, [40, 72, 40], clamp(soil.rain * 1.5 - 0.42, 0, 1) * 0.55);
  // dry ground goes to straw — but only where straw grows. Above the treeline
  // the same dryness is bare rock, and warming it there is half of why the high
  // country used to photograph as beach sand.
  c = mixRgb(c, [126, 116, 76], clamp(0.32 - soil.rain, 0, 1) * 1.5 * 0.46 * (1 - stone));

  // Where the water goes, the land darkens and greens, and a watercourse keeps
  // a damp margin either side of it. One continuous function of accumulated
  // flow: the blue is wet ground, not water — `render/rivers.ts` lays real
  // ribbons over every channel, at every distance.
  const wet = Math.log(1 + soil.flow);
  const drain = clamp(wet / 8, 0, 1);
  c = mixRgb(c, [38, 62, 44], drain * 0.38);
  // Damp blue-grey along watercourses — HALF what it was: this tint used to
  // do water's whole job, and once real ribbons lay in the channels it did
  // it too well, camouflaging them against their own margins. The ground
  // now says "wet"; the water says "water".
  c = mixRgb(c, [44, 76, 96], smoothstep(4.6, 9.2, wet) * 0.26);
  // A century of floods leaves its bed showing: pale gravel and silt along
  // the strong channels, lying between the water and the damp green margin —
  // and only on gentle ground, because a gorge wall keeps no bars. The same
  // reading feeds the material weights below, so the corridor doesn't just
  // tint like sediment, it *grains* like it.
  const sediment = smoothstep(6.0, 9.4, wet) * (1 - smoothstep(0.18, 0.40, slope));
  c = mixRgb(c, [116, 110, 94], sediment * 0.42);

  // wet valleys: rain gathered onto low ground grows the deepest green
  const lowland = 1 - smoothstep(0.12, 0.4, rel);
  c = mixRgb(c, [26, 54, 32], clamp(soil.rain * 1.5 - 0.4, 0, 1) * lowland * drain * 0.5);

  // A convex surface sheds its water, and what that leaves behind depends on
  // where it stands: dry pale grass on a lowland spur, scoured rock on a crest,
  // and the hollows between them hold the darkest growth. Now that `convexity`
  // measures a whole region cell it actually returns something, so these are
  // weaker than the numbers that never fired.
  const cvx = convexity(region, wx, wz);
  if (cvx > 0) {
    c = mixRgb(c, [122, 116, 82], cvx * 0.30 * (1 - montane));
    c = mixRgb(c, [88, 96, 112], cvx * 0.22 * stone);
  } else c = mixRgb(c, [40, 58, 38], -cvx * (1 - rel) * 0.26);

  // Fine grain, keyed at a granularity the finest mesh can actually resolve —
  // 4 m, against LOD 0's 4 m spacing — so it is one value per place rather than
  // one value per rung. Keyed at 1 m it aliased, which is why it used to be
  // faded out with distance, which is what made the rings visible.
  const n1 = hash01(Math.round(wx / 4), Math.round(wz / 4), 11);
  const n2 = hash01(Math.round(wx / 17), Math.round(wz / 17), 13);
  c = mixRgb(c, mixRgb(c, [56, 52, 36], 0.55), n1 * 0.085 + n2 * 0.15);

  // the three material bands, in order of height
  c = mixRgb(c, [92, 86, 62], montane * 0.40);        // dry earth and gravel
  c = mixRgb(c, [40, 50, 66], stone * 0.90);          // cold dark blue-grey rock
  c = mixRgb(c, [168, 176, 192], frost * 0.62);       // frost-bleached, still cool

  // The shore. A beach is what a *gentle* coast makes; a steep coast makes a
  // cliff, and a cliff is stone all the way down to the water — so the sand
  // has to ask the slope as well as the height, or every sea cliff on the
  // island gets painted with a beach it does not have. Its seaward end is not
  // gated here: `groundColour` already crossfades this whole palette into the
  // wet sand of the shallows across the waterline, which is what a wet beach is.
  const gentle = 1 - smoothstep(0.12, 0.34, slope);
  c = mixRgb(c, [138, 128, 100], (1 - smoothstep(1.2, 8, site.height)) * gentle * 0.85);
  // the tide's reach: sand within a couple of metres of the water is wet,
  // and wet sand is dark — the strongest cue there is that sea meets land.
  // Gated by `gentle` for the same reason the sand is: a cliff coast has no
  // strand for the tide to wet, and its wave-cut term below already darkens.
  c = mixRgb(c, [64, 60, 50], (1 - smoothstep(1.6, 5.0, site.height)) * gentle * 0.5);
  // and wave-cut rock below the splash line: darker and colder than the ground above
  c = mixRgb(c, [62, 66, 72],
             smoothstep(0.30, 0.62, slope) * (1 - smoothstep(24, 170, site.height)) * 0.55);

  // Steep ground sheds its soil in two stops: dry earth, then bare rock. The
  // rock is warm and brown in the foothills and cold grey in the high country,
  // so a face reads as *where it is* and not merely as steep.
  //
  // Both are gated hard by `stone`, and that gate is the difference between a
  // mountain and a dune. These two run *after* the material bands, high country
  // is steep by definition, and the alpine face they used to reach for was
  // paler than the rock band above it — so the high country darkened for one
  // line and was then lit straight back up again, which is most of why a
  // 2.5 km summit and a valley floor came out two counts apart.
  // The lower edge was calibrated in the TAIL of the slope it reads. Measured
  // island-wide on the lattice this loop uses (2,134,965 land samples, 4 m
  // central difference): p25 0.099, p50 0.216, p75 0.481. At 0.20 the earth
  // ramp returns 0.006 at the median slope and 0.91 by p75 — so across most of
  // the island slope contributed *nothing* to ground colour, and then switched
  // on almost at once over a quarter of it. That is a large part of why gentle
  // ground reads as one green: the one term that could tell a hollow from a
  // shoulder was off wherever the land was gentle enough for it to matter.
  //
  // 0.10 puts the median at 0.21 of the ramp rather than 0.006 — a hillside
  // gains a little earth where it steepens, which is what makes it read as a
  // hillside — while p25 stays at 0 so flats, beaches and meadows are exactly
  // the colour they were measured to be. The upper edge and both weights are
  // untouched, so nothing above p75 moves at all.
  const earth = smoothstep(0.10, 0.50, slope);
  const bare = smoothstep(0.50, 1.05, slope);
  c = mixRgb(c, [96, 82, 60], earth * 0.5 * (1 - stone * 0.85));
  c = mixRgb(c, mixRgb([84, 76, 64], [42, 50, 64], stone), bare * 0.75);

  // ---- and what all of that is *made of*, for the shader's grain ----------
  // Weights over the four texture families, from the readings already in
  // hand: rock wherever the stone band or a bare face or the frost cap won,
  // sand on the beach, the parched flats and the montane gravel belt, grass
  // where rain grows a living cover (a closed canopy keeps most of its floor
  // as litter and soil instead), and broken soil as everything's remainder.
  const dryness = clamp((0.32 - soil.rain) * 1.5, 0, 1);
  const green = clamp(soil.rain * 1.5 - 0.35, 0, 1) * (1 - dryness);
  const mRock = Math.max(stone * 0.95, bare * 0.9, frost * 0.85);
  const mSand = Math.max(
    (1 - smoothstep(1.2, 8, site.height)) * gentle,
    dryness * 0.7,
    montane * 0.35,
    sediment * 0.6,
  ) * (1 - mRock);
  const mGrass = Math.max(green * (b.woody ? 0.4 : 1), drain * 0.5)
    * (1 - earth * 0.55) * (1 - mRock) * (1 - mSand);
  const mSoil = Math.max(0.12, 1 - mRock - mSand - mGrass);
  const mSum = mGrass + mSoil + mSand + mRock;
  matOut[0] = mGrass / mSum;
  matOut[1] = mSoil / mSum;
  matOut[2] = mSand / mSum;
  matOut[3] = mRock / mSum;
  // …and how wet it is (2026-08-18): the same two readings the tint above
  // darkens by — rain, and the drainage that gathers it — as one number the
  // shader can put a surface to. Rain past 0.55 counts (`ui/subject.ts` calls
  // ground over 0.62 "wet country"; this starts a little under so the phrase
  // never fires on ground that looks dry), a watercourse's margin counts more,
  // a valley floor holds it and a spur sheds it, a steep face drains, and
  // stone is never wet ground however much falls on it.
  const rainWet = smoothstep(0.55, 0.88, soil.rain);
  matOut[4] = clamp(rainWet * (0.45 + 0.35 * lowland) + smoothstep(0.2, 0.75, drain) * 0.7, 0, 1)
    * (1 - smoothstep(0.22, 0.55, slope)) * (1 - stone) * (1 - Math.max(0, cvx) * 0.6);
  return c;
}

/**
 * Build one chunk. `detail` and `wide` are properties of the LOD, not of the
 * camera — the same chunk at the same LOD is always identical.
 */
export function buildChunk(region: Region, cx: number, cz: number, lod: number): ChunkData {
  const res = LOD_RES[Math.min(lod, LOD_RES.length - 1)];
  const step = CHUNK_SIZE / (res - 1);
  const ox = cx * CHUNK_SIZE, oz = cz * CHUNK_SIZE;
  /**
   * **Every rung draws the same land.** The LOD chooses how densely the surface
   * is sampled and nothing else.
   *
   * This was `1 / 0.75 / 0.4 / 0.15` from the initial commit and had never been
   * measured. `tools/lodfit.ts` measured it, and the ladder was doing the
   * opposite of its job. The fine term is four fbm groups at known wavelengths:
   * the *large* ones — 289 m, 143 m, 100 m, carrying ±24 m of the ±28 m budget —
   * are drawn perfectly well by a 32 m lattice, and only ±2.5 m of content sits
   * below what LOD 3 can represent. Scaling all four groups by one number threw
   * away the relief the rung could draw in order to suppress the little it could
   * not, so a coarse rung was simultaneously too flat and no less aliased:
   * **LOD 3 drew relief of sd 0.84 m where the land itself has 5.24 m.**
   *
   * It cost more than flatness. Everything that STANDS on the ground resolves
   * its own y through `surfaceHeight(..., 1)` — structures, anchors, rocks,
   * rivers, falls, litter, mortals, herds, path ribbons, fire — so past 768 m,
   * where LOD 1 starts, the mesh and the things on it were different surfaces.
   * Measured as the bilinear gap an object actually floats over:
   *
   *     rung   used from        was (med/p90/max)      now
   *     LOD 1     768 m      0.62 / 2.30 / 6.27 m    0.09 / 0.41 / 1.89 m
   *     LOD 2    1536 m      1.47 / 5.47 / 15.36     0.23 / 0.95 / 10.19
   *     LOD 3    3072 m      2.07 / 7.67 / 21.39     0.37 / 1.61 /  5.26
   *
   * Two more things fall out for free. Every shared edge between two rungs is
   * now **exactly zero** — both sides evaluate one pure function at one position
   * — where it was a median 0.6 m and up to 8.2 m (`tools/seam.ts`), which is
   * the dashed hairline at different-LOD boundaries that ROADMAP.md §13 records
   * as identified-but-unfixed. And a plant no longer changes height when its
   * chunk changes rung, which is law 8 read literally: `buildVegetation` seats
   * on this same value.
   *
   * The one thing it makes worse, measured rather than discovered later: past
   * the streamed ring `farField.ts` and `horizon.ts` sample detail 0, correctly,
   * because 156.5 m per vertex cannot carry a 30 m wave at all. LOD 3 used to be
   * nearly as flat as they are, so that rim was quiet; the step there goes from
   * a median 0.36 m to 2.42 m — 0.13 px to 0.84 px at 3 km, p90 3.11 px. That
   * boundary is between two genuinely different representations and will always
   * disagree; it was only quiet because the near rung had been flattened to meet
   * it, which is the bug. The chunk skirt already covers the crack.
   */
  const detail = 1;

  const height = new Float32Array(res * res);
  for (let j = 0; j < res; j++) for (let i = 0; i < res; i++)
    height[j * res + i] = surfaceHeight(region, ox + i * step, oz + j * step, detail);

  // one row of the pure field past each edge, so border derivatives can be
  // central. A neighbour at the same LOD samples these exact positions as
  // its own interior and lands on identical floats — the seam disagreement
  // this removes was one-sided differencing, never the field itself.
  const apron = {
    n: new Float32Array(res), s: new Float32Array(res),
    w: new Float32Array(res), e: new Float32Array(res),
  };
  for (let i = 0; i < res; i++) {
    apron.n[i] = surfaceHeight(region, ox + i * step, oz - step, detail);
    apron.s[i] = surfaceHeight(region, ox + i * step, oz + CHUNK_SIZE + step, detail);
    apron.w[i] = surfaceHeight(region, ox - step, oz + i * step, detail);
    apron.e[i] = surfaceHeight(region, ox + CHUNK_SIZE + step, oz + i * step, detail);
  }

  const colour = new Float32Array(res * res * 3);
  const material = new Float32Array(res * res * 4);
  const wet = new Float32Array(res * res);
  const mat: MatOut = [0, 0, 0, 0, 0];
  for (let j = 0; j < res; j++) for (let i = 0; i < res; i++) {
    const k = j * res + i;
    const l = i > 0 ? height[j * res + i - 1] : apron.w[j];
    const r = i < res - 1 ? height[j * res + i + 1] : apron.e[j];
    const u = j > 0 ? height[(j - 1) * res + i] : apron.n[i];
    const d = j < res - 1 ? height[(j + 1) * res + i] : apron.s[i];
    const slope = Math.hypot((r - l) / (2 * step), (d - u) / (2 * step));
    const c = groundSurface(region, ox + i * step, oz + j * step, slope, mat);
    colour[k * 3] = c[0] / 255; colour[k * 3 + 1] = c[1] / 255; colour[k * 3 + 2] = c[2] / 255;
    material[k * 4] = mat[0]; material[k * 4 + 1] = mat[1];
    material[k * 4 + 2] = mat[2]; material[k * 4 + 3] = mat[3];
    wet[k] = mat[4];
  }

  return { cx, cz, lod, res, height, colour, material, wet, apron,
           vegetation: buildVegetation(region, cx, cz, lod, detail) };
}

/**
 * What each biome grows, as weights over the eight kinds. A weight is a share,
 * not a probability — `standCharacter` tilts them by the land and `pickKind`
 * normalises. This lives here rather than on the biome table because it is a
 * fact about generation, not about the colour of the ground.
 *
 * The four right-hand columns are what separate biomes that used to be
 * indistinguishable in form; see `VegKind` for which pair each one splits.
 * Two rows carry most of that work:
 *
 * - **oak** moved its bulk from `broadleaf` to `spreading`. An oak woodland
 *   and a riparian forest were 0.03 apart because both were ~70% `broadleaf`,
 *   and the difference between them in life is exactly crown shape: a river
 *   tree grows up toward the light, an oak grows out.
 * - **alpine** lost its `broadleaf` entirely. It had 2%, which put occasional
 *   broad crowns on 2,500 m summits — the classifier saying something it
 *   should not — and gained `cushion`, which is what actually lives there.
 */
/**
 * The six right-hand columns widened the table a second time (v0.4). Each buys
 * a *place* rather than a pair this time: palms hold the warm coast, willows
 * and poplars the water margins, ferns the damp forest floor, flowers the
 * meadows and the alpine turf, and snags stand wherever trees grow and
 * sometimes fail — which is every woody biome, and is what makes a forest
 * read as old rather than planted. Every column still sums with its row to
 * ~1.0, and `tools/form.ts` is still the instrument that judges the result.
 */
/**
 * And the seven right-hand columns are v0.5's, aimed at the nine pairs
 * `tools/form.ts` still named. What is different about this pass: the six
 * biomes involved are all SHORT, so the previous rule — pick a silhouette,
 * check the probe — is the rule §13 already recorded failing on marsh. Every
 * row below therefore changes TWO things at once, and the second is the one
 * that shows: the biome's tall column, and the biome's own FLOOR (`LOW_COVER`
 * below), because on ground where nothing clears two metres the floor is most
 * of what the eye is looking at.
 *
 * Two rows do more than add a column:
 *
 * - **alpine** lost almost all its `shrub` (0.14 → 0.04) to `krummholz`. §13
 *   left this open — "alpine is still 53% shrub, the biome's own weights want
 *   revisiting rather than the tilt" — and it was right that the tilt was not
 *   the fault: the treeline branch tests `rel > ~0.525` (about 1,350 m) while
 *   the alpine BIOME starts far below that, so most alpine ground never took
 *   the high-ground path and filled in with lowland shrub. `LOW_COVER` fixes
 *   the floor and this fixes the ceiling.
 * - **beach** halved its `tuft` for `creeper`, which nothing else grows at
 *   weight. Shore was under 0.25 from four other biomes and 56% tuft was why.
 */
export const KIND_MIX: Record<BiomeKey, KindWeights> = {
  //            broad  conif  shrub   tuft  spread   reed rosett cushion column   palm   snag   fern flower willow  succul  plume  broom creepr  umbel krummh candle
  ocean:     [ 0.00, 0.00, 0.00, 1.00,  0.00, 0.00, 0.00, 0.00,  0.00, 0.00, 0.00, 0.00, 0.00, 0.00,  0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
  beach:     [ 0.03, 0.00, 0.14, 0.34,  0.00, 0.04, 0.00, 0.00,  0.00, 0.10, 0.01, 0.00, 0.03, 0.00,  0.00, 0.05, 0.00, 0.20, 0.06, 0.00, 0.00],
  marsh:     [ 0.02, 0.00, 0.08, 0.18,  0.00, 0.52, 0.00, 0.00,  0.02, 0.02, 0.01, 0.00, 0.05, 0.06,  0.00, 0.00, 0.00, 0.00, 0.04, 0.00, 0.00],
  riparian:  [ 0.56, 0.04, 0.10, 0.05,  0.02, 0.03, 0.00, 0.00,  0.05, 0.01, 0.02, 0.04, 0.00, 0.07,  0.00, 0.00, 0.00, 0.00, 0.01, 0.00, 0.00],
  redwood:   [ 0.10, 0.72, 0.04, 0.01,  0.00, 0.00, 0.00, 0.00,  0.00, 0.00, 0.03, 0.10, 0.00, 0.00,  0.00, 0.00, 0.00, 0.00, 0.00, 0.00, 0.00],
  oak:       [ 0.09, 0.02, 0.10, 0.05,  0.60, 0.00, 0.00, 0.00,  0.02, 0.00, 0.04, 0.01, 0.04, 0.00,  0.00, 0.03, 0.00, 0.00, 0.00, 0.00, 0.00],
  chaparral: [ 0.03, 0.00, 0.30, 0.14,  0.00, 0.00, 0.10, 0.00,  0.00, 0.00, 0.05, 0.00, 0.03, 0.00,  0.00, 0.00, 0.34, 0.00, 0.00, 0.00, 0.01],
  grassland: [ 0.04, 0.01, 0.06, 0.46,  0.02, 0.00, 0.00, 0.00,  0.01, 0.00, 0.01, 0.00, 0.09, 0.00,  0.00, 0.24, 0.00, 0.00, 0.06, 0.00, 0.00],
  scrub:     [ 0.03, 0.01, 0.38, 0.20,  0.00, 0.00, 0.05, 0.00,  0.00, 0.00, 0.03, 0.00, 0.09, 0.00,  0.00, 0.04, 0.00, 0.00, 0.17, 0.00, 0.00],
  // succulent carries a weight far above the share it lands at — set this cell
  // to 0.24 and `tools/form.ts` reports 4% of dry scrub's plants, at the
  // shipped 0.34 it reports 5% — because the
  // smoothstep gate in `standCharacter` is doing most of its work: the tilt is
  // evaluated at the plant's `soilAt`-jittered position, not at the chunk
  // centre the field table was measured at, so a good part of a dry-scrub
  // chunk's plants stand on soil that is not desert-dry at all. Island-wide
  // that is 3,624 succulents (`tools/form.ts --census`), 3,268 of them in dry
  // scrub and 356 in chaparral — the gate holds, and nothing leaks further.
  desert:    [ 0.01, 0.00, 0.14, 0.14,  0.00, 0.00, 0.26, 0.00,  0.00, 0.02, 0.06, 0.00, 0.00, 0.00,  0.34, 0.00, 0.00, 0.00, 0.00, 0.00, 0.03],
  conifer:   [ 0.07, 0.71, 0.05, 0.02,  0.00, 0.00, 0.00, 0.00,  0.00, 0.00, 0.04, 0.08, 0.00, 0.00,  0.00, 0.00, 0.00, 0.00, 0.00, 0.03, 0.00],
  alpine:    [ 0.00, 0.05, 0.04, 0.16,  0.00, 0.00, 0.00, 0.44,  0.00, 0.00, 0.02, 0.00, 0.09, 0.00,  0.00, 0.00, 0.00, 0.00, 0.00, 0.20, 0.00],
};

/**
 * What fills in UNDER and BETWEEN a stand, per biome — `[fine, coarse]`.
 *
 * This was `tuft`-or-`shrub` everywhere, and §13 already caught half of what
 * that costs: on open ground `understory` reaches 0.40, so **two plants in
 * five are chosen before the biome's own mix is ever consulted**. The other
 * half is what the probe could not see. The nine pairs still under 0.25 were
 * all between short open biomes, and the thing those biomes have most of is
 * exactly this floor — so as long as every one of them floored in the same two
 * shapes, no amount of work on the tall columns could separate them. A dry
 * scrub floors in rosettes and gravel; a strand floors in creeping mat; a
 * chaparral floors in broom; a grassland floors in bunchgrass. They are
 * different countries at ankle height before they are different at eye height.
 *
 * Above the treeline the low cover is `cushion` whatever this table says —
 * that override is older than this table and still correct, because a cushion
 * is an answer to wind rather than to soil.
 */
const LOW_COVER: Record<BiomeKey, readonly [VegKind, VegKind]> = {
  ocean:     ['tuft', 'tuft'],
  beach:     ['tuft', 'creeper'],
  marsh:     ['tuft', 'reed'],
  riparian:  ['fern', 'shrub'],
  redwood:   ['fern', 'shrub'],
  oak:       ['plume', 'shrub'],
  chaparral: ['tuft', 'broom'],
  grassland: ['tuft', 'plume'],
  scrub:     ['tuft', 'shrub'],
  // Both slots, and it is the entry that finally split the island's last pair.
  // A dry scrub floor is rosettes and bare gravel — it is not a thin grassland,
  // and while `tuft` sat in the fine slot the two driest biomes shared their
  // floor and stayed 0.24 apart whatever their tall columns did. Grass still
  // reaches dry scrub through the mix (0.24) and through `KIND_DRIFT`; what it
  // no longer does is arrive automatically under every stand.
  desert:    ['rosette', 'rosette'],
  conifer:   ['fern', 'shrub'],
  alpine:    ['cushion', 'cushion'],
};

/**
 * What each kind is COLOURED like — three targets per kind, not one.
 *
 * The three are a triangle around the species' real colour, not three
 * arbitrary tints: for a broadleaf they are a yellow-green, a deep green and
 * an olive, which is the range one species genuinely holds across ages,
 * aspects and individuals. The old single target is roughly their centroid in
 * every row, so no biome's mean colour moves; what changes is that a stand
 * spans the triangle instead of standing on the middle of it. `tools/palette.ts`
 * measured the biome tints and this does not touch them — the mix here starts
 * from the biome colour that probe fixed.
 *
 * `absolute` means the targets replace the leaf colour rather than mixing with
 * it, which only dead wood wants.
 */
interface KindCast {
  to: readonly [
    readonly [number, number, number],
    readonly [number, number, number],
    readonly [number, number, number]];
  /** Mix strength toward the chosen target, before the per-instance jitter. */
  t: number;
  absolute?: true;
}
const KIND_CAST: Record<VegKind, KindCast> = {
  broadleaf: { to: [[96, 120, 52], [72, 104, 58], [104, 110, 66]], t: 0.30 },
  conifer:   { to: [[30, 58, 54], [42, 70, 44], [26, 52, 46]], t: 0.46 },
  // an oak canopy is greyer and duller than a river tree's, which is half of
  // why the two read apart on the same hillside
  spreading: { to: [[96, 106, 50], [80, 98, 58], [108, 110, 62]], t: 0.34 },
  shrub:     { to: [[100, 106, 54], [82, 96, 62], [110, 104, 70]], t: 0.34 },
  tuft:      { to: [[158, 146, 78], [128, 140, 84], [166, 150, 96]], t: 0.36 },
  // reeds go straw at the tips wherever they are not standing in water
  reed:      { to: [[136, 138, 70], [116, 132, 80], [146, 140, 86]], t: 0.40 },
  // glaucous: the blue-grey wax of a plant that has to keep its water
  rosette:   { to: [[100, 124, 98], [116, 124, 84], [92, 112, 90]], t: 0.46 },
  cushion:   { to: [[76, 92, 58], [66, 82, 64], [88, 92, 66]], t: 0.42 },
  // a poplar's crown is a shade lighter and yellower than the gallery forest
  // it stands over — it reads as a lit candle among the green
  columnar:  { to: [[110, 126, 52], [92, 116, 60], [116, 122, 68]], t: 0.38 },
  palm:      { to: [[98, 132, 54], [84, 118, 64], [110, 128, 66]], t: 0.44 },
  snag:      { to: [[124, 114, 96], [96, 88, 76], [140, 132, 116]], t: 1, absolute: true },
  fern:      { to: [[42, 90, 42], [52, 84, 52], [38, 78, 50]], t: 0.52 },
  // a flush of bloom — gold, dusty pink or white. The row that proved the
  // model: it is the only kind that already picked per instance, and it is the
  // only kind the probe measured as varying in hue at all.
  flower:    { to: [[168, 150, 84], [162, 118, 122], [158, 158, 148]], t: 0.62 },
  willow:    { to: [[102, 122, 54], [86, 112, 64], [110, 118, 70]], t: 0.42 },
  // waxy blue-green, and it must not read as any green beside it: this is the
  // colour half of what makes dry scrub stop looking like coastal scrub
  succulent: { to: [[96, 124, 104], [110, 130, 96], [84, 112, 100]], t: 0.54 },
  // pale seed heads over green blades — the plume IS the straw
  plume:     { to: [[156, 148, 104], [172, 158, 112], [138, 140, 96]], t: 0.46 },
  // olive-grey wands with almost no leaf on them
  broom:     { to: [[110, 112, 74], [96, 106, 66], [124, 118, 84]], t: 0.48 },
  creeper:   { to: [[110, 130, 100], [96, 124, 96], [124, 134, 106]], t: 0.48 },
  // the head is nearly white and it is the whole signal; the stalk keeps its
  // own green in the geometry, exactly as `flower`'s does
  umbel:     { to: [[176, 176, 160], [186, 180, 150], [162, 168, 152]], t: 0.42 },
  // wind-burnt, and the darkest foliage on the island — a krummholz mat reads
  // as a shadow on the fellfield, which is what it looks like
  krummholz: { to: [[40, 58, 46], [34, 52, 44], [50, 64, 48]], t: 0.52 },
  candle:    { to: [[166, 156, 118], [180, 168, 124], [150, 142, 112]], t: 0.52 },
};

/**
 * How far red and blue may be pushed against green, per instance.
 *
 * **Swept, not chosen.** `tools/foliage.ts` section 6 reports mean stand
 * |Δchromaticity| across the kinds — 260 chunks walked across the whole
 * island, pairs of the same species within 40 m of each other. Every row below
 * is a run of that section; the jitter rows come from setting this constant
 * and re-running, since it is a compile-time constant and there is no flag:
 *
 * | model | mean stand \|Δhue\| |
 * |---|---:|
 * | **the shipped model** (`git show HEAD:src`) | **0.0100** |
 * | …and with `flower` flattened too — never shipped | 0.0065 |
 * | three targets, no jitter | 0.0187 |
 * | three targets + 0.035 | 0.0210 |
 * | three targets + 0.045 | 0.0222 |
 * | **three targets + 0.055** | **0.0235** |
 * | three targets + 0.075 | 0.0264 |
 * | three targets + 0.10 | 0.0306 |
 *
 * The second row is here because it is the trap. It is tempting to call the
 * baseline "one fixed target per kind" and measure that — but `flower` never
 * was one fixed target, it has always dealt gold / dusty pink / white per
 * instance, and it alone carries a third of the island's hue variation.
 * Flattening it too gives a prettier headline (3.6× rather than 2.35×) for a
 * state the game was never in. **The honest before is 0.0100 and the honest
 * factor is 2.35×.**
 *
 * Two things fall out of the rest of the table, and the second decided the
 * constant. **The three targets do most of the work** — 0.0100 → 0.0187 is
 * 1.9× before this constant does anything at all, and the jitter carries the
 * remaining 1.26× — so the structural change was the real fix and this is a
 * finish on it. And **the ladder is nearly flat**: doubling from 0.055 to 0.10
 * buys 30%, so there is no cliff to find and the choice is a judgement about
 * where to stop rather than an optimum.
 *
 * 0.055 stops where the carrying kinds sit at about 0.023, which is a bit over
 * twice the old model and still well under `flower`'s 0.0558 — flower being
 * the kind that has always picked its head colour per instance, and the one
 * the probe's own verdict calls "wide". A stand should read as many
 * individuals of ONE species. Past about 0.08 the greens start clustering
 * into separable populations, which is a different plant, not a different
 * individual, and that is `world/strain.ts`'s job rather than this one's.
 */
const HUE_JITTER = 0.055;

/** How far a plant may stray from its stand's species. Edges mix; hearts don't. */
const KIND_DRIFT = 0.14;
/** Clump attempts per chunk. The density field decides how many survive. */
const CLUMPS = 56;

/**
 * How much of the understory each rung keeps — and why it is a *fraction* and
 * not a height cut.
 *
 * The ladder used to be three height cuts, 0 / 0.9 / 4.0 m, with the grass
 * existing only on rung 0. `lodFor` puts rung 0 out to three chunks and rung 1
 * out to six, so every blade of grass in the world stopped at a square 1024 m
 * from the focus: a hard-edged quadrilateral of bare ground in the middle
 * distance. That is the same class of artifact as ground colour that reads its
 * own LOD — a property of the streamer showing up as a shape in the land.
 *
 * What replaces it: every candidate plant carries a rank hashed from **its own
 * position**, and a rung keeps the plants whose rank falls under a fraction
 * that depends on the rung and the plant's height. Small plants thin fastest,
 * a tree does not thin at all, and the understory therefore fades over three
 * gentle steps — roughly 100 / 60 / 27 percent at 1.0, 1.8 and 3.1 km — where
 * it used to be deleted in one. A density change reads as distance; a category
 * vanishing reads as an edge.
 *
 * **`keepFraction` is monotonically non-increasing in `lod` at every height**,
 * which is the whole reason a rank threshold is a legal thinning: the kept set
 * at a coarse rung is then always a subset of the kept set at a finer one. It
 * holds by construction, not by luck — the fraction is the `max` of two terms
 * that are each non-increasing in `lod` on their own:
 *
 * - `KEEP_FLOOR` falls as the rung coarsens;
 * - the smoothstep's window (`KEEP_FULL_LO`/`KEEP_FULL_HI`) only ever slides
 *   *right*, and sliding a smoothstep right can only lower it;
 *
 * and `max` of two non-increasing functions is non-increasing.
 * `test/lod-stability.ts` asserts it numerically over the whole height range
 * as well, because the tables are the kind of thing a later hand will tune.
 */
const KEEP_FLOOR   = [1.00, 0.60, 0.26, 0.00] as const;
/** Height at which a rung stops thinning at all — the start of its full-keep ramp. */
const KEEP_FULL_LO = [0.00, 0.90, 1.80, 1e4] as const;
/** …and the end of it: every plant at least this tall survives the rung intact. */
const KEEP_FULL_HI = [1.00, 3.20, 4.40, 2e4] as const;

/**
 * Taller than the size laws below can ever produce. A rung whose keep fraction
 * is still zero here can keep nothing at all, and says so before it spends any
 * noise on a chunk — which is how rung 3 stays the cheap rung it has to be.
 */
const TALLEST_PLANT = 60;

/**
 * The share of candidate plants `metres` tall that rung `lod` keeps, in [0, 1].
 * Non-increasing in `lod` at every height — see the tables above.
 */
export function keepFraction(lod: number, metres: number): number {
  const l = Math.min(Math.max(lod, 0), KEEP_FLOOR.length - 1);
  return Math.max(KEEP_FLOOR[l], smoothstep(KEEP_FULL_LO[l], KEEP_FULL_HI[l], metres));
}

export type KindWeights = readonly [
  number, number, number, number, number, number, number, number,
  number, number, number, number, number, number,
  number, number, number, number, number, number, number];

/**
 * The kinds in weight order — one place, so no table can drift out of step.
 *
 * Exported because `tools/form.ts` needs a runtime list of the kinds and had
 * been carrying its own copy. `VegKind` is a type and vanishes at runtime, so
 * the copy could not fail to compile — it just silently stopped counting the
 * seven kinds v0.5 added, reported the mix percentages diluted by plants it
 * was not counting, and said the vocabulary had not moved. A probe that
 * reports "no change" because it cannot see the change is worse than no probe,
 * and this is the only structural fix for it: one list, imported.
 */
export const KIND_ORDER: readonly VegKind[] = [
  'broadleaf', 'conifer', 'shrub', 'tuft', 'spreading', 'reed', 'rosette', 'cushion',
  'columnar', 'palm', 'snag', 'fern', 'flower', 'willow',
  'succulent', 'plume', 'broom', 'creeper', 'umbel', 'krummholz', 'candle',
];

/** The kinds that make a stand woody — a clump of these crowds like a grove. */
const WOODY_KINDS = new Set<VegKind>([
  'broadleaf', 'conifer', 'spreading', 'columnar', 'palm', 'snag', 'willow',
]);

/** What kind of stand a spot grows, and how big it lets its plants get. */
interface StandCharacter {
  /** Species weights, in `KIND_ORDER`. */
  mix: KindWeights;
  /** Height multiplier: 1 is a rich lowland, well under 1 is thin ground. */
  stature: number;
}

/**
 * The character of the stand at one spot: the biome's own mix, tilted by the
 * land it stands on. High ground trades broad crowns for spires; a watercourse
 * trades spires for broadleaf; dry ground trades trees for mounds and grass.
 *
 * `stature` is the other half of the same idea — the same species is a giant on
 * deep wet lowland soil and a stunted thing at treeline or on a dry flat, and
 * without it a dithered biome edge drops a thirty-metre conifer onto a desert.
 *
 * Pure function of the site, so a slope's character is the same character in
 * every worker, at every LOD.
 */
function standCharacter(b: Biome, site: Site, region: Region, wet: number): StandCharacter {
  const m = KIND_MIX[b.key];
  const rel = clamp(site.height / (region.maxHeight * region.heightScale), 0, 1);
  const alpine = smoothstep(0.30, 0.75, rel);
  const dry = clamp(0.34 - site.rain, 0, 1) * 2;
  return {
    mix: [
      m[0] * (1 - alpine * 0.7) * (1 - dry * 0.5) + wet * 0.22,
      m[1] * (1 + alpine * 1.6) * (1 - wet * 0.4),
      m[2] * (1 + dry * 0.8),
      m[3] * (1 + dry * 0.5),
      // a spreading crown wants the same deep, unfrozen ground a broad one
      // does, and wants it more: it is the shape of a tree with room and time
      m[4] * (1 - alpine * 0.85) * (1 - dry * 0.6) + wet * 0.10,
      // reeds are standing water made visible; drought is the end of them
      m[5] * (1 - dry) + wet * 0.55,
      // A rosette is the opposite plant in every respect — but dry is not the
      // same as *cold* and dry, and without the altitude term a summit reads
      // as dry ground and grows agaves at 2,500 m. `tools/form.ts` caught it:
      // alpine was fielding 12% rosette.
      m[6] * (1 + dry * 1.4) * (1 - wet * 0.9) * (1 - alpine),
      // a cushion is a wind answer, not a soil one: it appears where nothing
      // taller will stand, whatever the biome table said lower down
      m[7] * (1 + alpine * 1.2) + alpine * 0.10,
      // a column is a river-margin tree: it wants running water more than a
      // broad crown does, and it dies out of it faster
      m[8] * (1 - alpine) * (1 - dry * 0.8) + wet * 0.16,
      // palms hold the warm low coast; altitude ends them before drought does
      m[9] * (1 - alpine),
      // standing dead wood is where trees grew and then failed: drought raises
      // it, and the wet lowland recycles its dead too fast to keep any
      m[10] * (1 + dry * 1.2) * (1 - wet * 0.5),
      // ferns are shade-and-damp understory; drought is the end of them
      m[11] * (1 - dry) * (1 - alpine * 0.6) + wet * 0.10,
      // forbs flush wherever the rain reaches — meadow and alpine turf alike
      m[12] * (1 + clamp(site.rain, 0, 1) * 0.8),
      // a willow is the waterline made visible
      m[13] * (1 - dry) * (1 - alpine) + wet * 0.28,
      /**
       * A succulent is a water TANK, so it is not merely dry-tolerant like the
       * shrubs — it belongs to the driest ground on the island and nowhere
       * else. **The gate is a measured threshold, not a slope**, and this is
       * where `tools/form.ts`' new field table earns its place: `dry` never
       * gets anywhere near 1 on this island, and its p10/p50/p90 are
       *
       *     chaparral  0.14 / 0.23 / 0.35
       *     dry scrub  0.37 / 0.44 / 0.48
       *
       * so a `(1 + dry · k)` term — which is what every other row here uses —
       * separates those two biomes by at most about a quarter however large k
       * is. That is why the first cut of this column leaked succulents into
       * chaparral and broom into dry scrub, and it is a general trap in this
       * table worth stating plainly: **a multiplicative dryness tilt cannot
       * split two dry biomes on this island.** A smoothstep placed in the gap
       * between chaparral's p90 (0.35) and dry scrub's p10 (0.37) can, and
       * does, exactly.
       *
       * The altitude term is not decoration. Alpine's `dry` p50 is 0.43 — as
       * dry as dry scrub — so on the drought axis alone a summit and a desert
       * are the same place, which is precisely how §13's agaves got to 2,500 m.
       * Alpine's `rel` p10 is 0.816 and `alpine` is `smoothstep(0.30, 0.75,
       * rel)`, so that factor is 1 up there and this term is exactly zero.
       */
      m[14] * smoothstep(0.34, 0.46, dry) * (1 - wet) * (1 - alpine),
      // bunchgrass is grass with a deeper root: it survives a drought a tuft
      // does not, so it *rises* a little as the ground dries before it goes
      m[15] * (1 + dry * 0.3) * (1 - dry * dry * 0.7) * (1 - alpine * 0.7),
      // Broom is sclerophyll — hard-leaved and dry-tolerant — but Mediterranean
      // dry, not desert dry, and the mirror of the succulent gate above is what
      // says so. Without it chaparral's broom washed into dry scrub across the
      // ecotone and left the two 0.24 apart, the last pair on the island still
      // wearing the same clothes.
      m[16] * (1 + dry * 0.6) * (1 - smoothstep(0.34, 0.46, dry))
            * (1 - wet * 0.7) * (1 - alpine * 0.8),
      // a strand mat is salt and sand: running water is the end of it, and it
      // has no business anywhere near a summit
      m[17] * (1 - wet * 0.6) * (1 - alpine),
      // umbellifers are damp-verge forbs; they follow rain and end in drought
      m[18] * (1 + clamp(site.rain, 0, 1) * 0.7) * (1 - dry * 0.9) * (1 - alpine * 0.6),
      // krummholz is the same wind answer a cushion is, one storey up: it is
      // what a conifer becomes where a conifer cannot stand, so it appears on
      // altitude alone and does not care what the biome table said lower down
      m[19] * (1 + alpine * 1.3) + alpine * 0.07,
      // a bolted rosette follows its own rosette exactly — same drought, same
      // altitude gate, and the rarity is carried in the weight, not the tilt
      m[20] * (1 + dry * 1.4) * (1 - wet * 0.9) * (1 - alpine * 0.8),
    ],
    stature: clamp(0.34 + b.density * 0.44 + clamp(site.rain * 1.3, 0, 1) * 0.42 + wet * 0.22,
                   0.34, 1.08) * (1 - alpine * 0.55),
  };
}

/** Weighted pick over the kinds, `u` in [0, 1). */
function pickKind(w: KindWeights, u: number): VegKind {
  let total = 0;
  for (const v of w) total += v;
  if (total <= 0) return 'tuft';
  let t = clamp(u, 0, 0.999999) * total;
  for (let i = 0; i < w.length; i++) if ((t -= w[i]) < 0) return KIND_ORDER[i];
  return KIND_ORDER[KIND_ORDER.length - 1];
}

/**
 * How closed the canopy is at a world position: 0 is genuine open ground, 1 is
 * the heart of a stand. Two scales do the work that one hash could not —
 *
 * - a broad ~1200 m field decides where forest wants to be *at all*, pushed
 *   through a smoothstep so it has plateaus: closed hearts and true clearings,
 *   not a gentle everywhere-wobble;
 * - a ~300 m field punches glades into those stands and feathers their edges,
 *   which is what gives a hillside a treeline instead of a texture.
 *
 * The biome's own `density` still sets the ceiling; this only says how much of
 * that ceiling this spot reaches. Deterministic fbm on world position, so a
 * forest edge is the same edge at every LOD and in every worker.
 */
function standField(region: Region, wx: number, wz: number): number {
  const s = region.seed;
  const stand = fbm(wx / 1200, wz / 1200, 3, s + 401);
  const grain = fbm(wx / 300, wz / 300, 3, s + 409);
  const closed = smoothstep(0.36, 0.60, stand);
  const glade = smoothstep(0.24, 0.58, grain);
  return clamp(closed * (0.32 + 0.85 * glade), 0, 1);
}

/**
 * Vegetation for one chunk. Seeded from the chunk's own coordinates, so a tree
 * placed here stays here — the fault that made the old build shimmer.
 *
 * Every LOD generates the *same* stand from the same rng stream; coarser rungs
 * simply omit part of the understory. A tree that exists is the same tree —
 * same spot, same size, same species — at every distance. Nothing may inflate,
 * move or re-species as the camera moves, which is why every random draw
 * happens before any filter: the stream must not know what rung it is on.
 */
function buildVegetation(region: Region, cx: number, cz: number, lod: number, detail: number): Instance[] {
  // a rung that would thin even the tallest tree to nothing grows nothing, and
  // finds that out before it touches the noise. Past rung 2 the far canopy
  // (`render/farCanopy.ts`) covers the same ground for two draw calls.
  if (keepFraction(lod, TALLEST_PLANT) <= 0) return [];
  const rnd = chunkRng(region.seed, cx, cz, 7);
  const ox = cx * CHUNK_SIZE, oz = cz * CHUNK_SIZE;
  const out: Instance[] = [];

  for (let c = 0; c < CLUMPS; c++) {
    const px = ox + rnd() * CHUNK_SIZE, pz = oz + rnd() * CHUNK_SIZE;
    const trueSite = region.siteAt(px, pz);
    if (!trueSite.land) continue;
    // stands read the same dithered soil the ground does, so tree species
    // blend across cell borders instead of switching along them
    const site = soilAt(region, px, pz, trueSite);
    if (!site.land) continue;
    const b = BIOMES[site.biome];
    const wet = clamp(Math.log(1 + site.flow) / 9, 0, 1);
    const canopy = standField(region, px, pz);
    let density = b.density * (0.5 + 0.8 * clamp(site.rain * 1.3, 0, 1) + wet * 0.5);
    // forest swings all the way to bare ground between its stands; open biomes
    // keep a floor, so a grassland thins and thickens instead of switching off
    density *= lerp(b.woody ? 0.06 : 0.40, 1.15, canopy);
    if (rnd() > clamp(density, 0, 1)) continue;

    const { mix, stature } = standCharacter(b, site, region, wet);
    // the same treeline reading `standCharacter` makes, kept here so the
    // understory can ask it too
    const highGround = smoothstep(0.30, 0.75,
      clamp(site.height / (region.maxHeight * region.heightScale), 0, 1)) > 0.5;
    const dominant = pickKind(mix, rnd());
    const woody = WOODY_KINDS.has(dominant);
    // a closed canopy shades its floor out; open ground fills in with grass
    const understory = lerp(0.40, 0.08, canopy);
    // clump tightness follows the density field: a dense stand crowds together,
    // sparse ground scatters lone plants across a wide skirt
    const fill = clamp(canopy * (0.6 + 0.5 * rnd()), 0, 1);
    const spread = lerp(woody ? 30 : 17, woody ? 8 : 5, fill);
    const many = Math.max(1, Math.round(lerp(woody ? 1.4 : 3, woody ? 11 : 17, fill)));
    const vigour = 0.6 + rnd() * 0.8;

    for (let k = 0; k < many; k++) {
      const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd()) * spread;
      const x = px + Math.cos(a) * r, z = pz + Math.sin(a) * r;
      const grow = rnd();               // size within the species
      const wide = rnd();               // crown or mound width
      const tall = rnd();               // the occasional emergent
      const tone = rnd();
      const shade = 0.9 + rnd() * 0.36;
      const rot = rnd() * Math.PI * 2;

      // Species: mostly the stand's own, but a per-plant hash on the plant's
      // own position drops understory in under the canopy and lets a fraction
      // drift back to the biome mix — so a forest edge mixes because the hash
      // says so, not because a biome border was crossed.
      const hk = hash01(Math.round(x * 3), Math.round(z * 3), region.seed + 611);
      // What fills in under and between the stand. It used to be tuft-or-shrub
      // everywhere, which quietly diluted every biome toward the same two
      // shapes — 40% of an open biome's plants were being chosen before its
      // own mix was ever consulted, and it is why alpine fielded 8% cushion
      // against a 0.48 weight. Above treeline the low cover *is* cushion, so
      // that is what the understory becomes there.
      // …and what those two shapes ARE is now the biome's business — see
      // `LOW_COVER`. Above the treeline both collapse to cushion, which is the
      // older rule and the correct one: nothing taller stands up there.
      const floor = LOW_COVER[site.biome];
      let kind: VegKind =
        hk < understory * 0.55 ? (highGround ? 'cushion' : floor[0])
        : hk < understory ? (highGround ? 'cushion' : floor[1])
        : hk > 1 - KIND_DRIFT ? pickKind(mix, (hk - (1 - KIND_DRIFT)) / KIND_DRIFT)
        : dominant;

      /**
       * The dead roll (v0.4): a little of everything that grows is standing
       * dead, hashed from the plant's own position so the same plant is dead
       * at every rung and in every worker. Dryness raises it, so a drought
       * edge wears a dead fringe before it becomes bare ground — which is a
       * boundary the eye believes, where a hard biome line is one it doesn't.
       * A dead *tree* becomes a snag outright (bare wood is what a dead tree
       * looks like); a low plant keeps its silhouette and loses its green.
       */
      const dry01 = clamp(0.34 - site.rain, 0, 1) * 2;
      const dead = kind !== 'snag'
        && hash01(Math.round(x * 6), Math.round(z * 6), region.seed + 0xdead)
           < 0.02 + dry01 * 0.07;
      if (dead && WOODY_KINDS.has(kind)) kind = 'snag';

      /**
       * Height in metres, and width as a multiple of it — the ratio is what
       * makes a spire a spire and a mound a mound.
       *
       * ## The trees roughly doubled (2026-08-21)
       *
       * They were too small, and the workshop's scale reference is what made
       * it undeniable: the island's median spreading oak drew **3.3 m** beside
       * a reference figure drawn **3.98 m**, so the oaks stood below the
       * shoulder of the people living under them. `render/tree/profile.ts`
       * carries what a mature tree of each species actually is; these are the
       * world's own numbers moved toward them.
       *
       * `Instance.scaleY` is a scale on the kind's BUILD, not metres on the
       * screen — drawn height is `scaleY × the kind's build height`, and the
       * build heights run 0.67 (spreading) to 1.07 (columnar). So a spreading
       * oak's 22 emergent is 14.8 m drawn, not 22.
       *
       * **Three readers take a height and each needed a decision**, which
       * `CLAUDE.md` names by hand:
       *
       *  · `world/wear.ts`'s `SPARED` — scaled with the trees, or camps stop
       *    clearing their own ground and every settlement sits in pristine
       *    forest, which is the failure its own comment describes.
       *  · `keepFraction`'s windows — deliberately NOT scaled. They top out at
       *    4.4 and govern the ground layer; every tree cleared them before and
       *    clears them now, so nothing changes for grass and nothing changes
       *    for trees.
       *  · `ui/attention.ts`'s `namePlant` — scaled for the tree kinds only.
       *    A willow was "young" under 4 m and every willow is over that now.
       *
       * The far builds are untouched: this moves the world's own sizes, not
       * the shapes, and `tools/form.ts`'s "as drawn" table is where the two
       * are read together.
       *
       * **The ratio spans widened in v0.5, and that is the per-individual half
       * of this lane.** They were about ±20% around each kind's mean — a
       * broadleaf ran 0.78–1.12, a ×1.44 span — which is why a hillside of
       * them photographed as one crown at a dozen scales: every tree really
       * WAS the same proportion, and scale alone reads as distance rather than
       * as a different tree. They now run about ×2.2–2.7, which is the span an
       * actual stand holds: a suppressed tree drawn up thin between its
       * neighbours and an open-grown one that spread instead. It costs
       * nothing — `wide` is a draw that was already being made and thrown at a
       * narrow range.
       *
       * Heights were deliberately NOT widened with them. `wear.ts` spares
       * plants over 18 m, `keepFraction`'s tables are height cuts, and
       * `namePlant` reads 6 m and 18 m as thresholds; moving the height
       * distribution moves all three, and none of that is this lane's to move.
       * Proportion is free of every one of them.
       */
      const emergent = tall < (kind === 'conifer' ? 0.22 : 0.18);
      let h: number, ratio: number;
      switch (kind) {
        case 'conifer':
          h = (emergent ? 30 + grow * 18 : 17 + grow * 15) * vigour * stature;
          ratio = 0.15 + wide * 0.26;
          break;
        case 'broadleaf':
          h = (emergent ? 21 + grow * 13 : 10 + grow * 9) * vigour * stature;
          ratio = 0.58 + wide * 0.80;
          break;
        // The whole point of `spreading` is the ratio, not the height: shorter
        // than a river tree and half again as wide, so an oak woodland reads
        // as a different country from the air even at the same stature.
        case 'spreading':
          h = (emergent ? 22 + grow * 12 : 11 + grow * 10) * vigour * stature;
          ratio = 1.20 + wide * 1.05;
          break;
        case 'shrub':
          h = (0.9 + grow * 1.7) * vigour * lerp(1, stature, 0.5);
          ratio = 0.80 + wide * 1.00;
          break;
        // tall for its width and always in company — a reedbed, not a plant
        case 'reed':
          h = (1.1 + grow * 1.5) * vigour * lerp(1, stature, 0.35);
          ratio = 0.30 + wide * 0.16;
          break;
        case 'rosette':
          h = (0.55 + grow * 0.85) * vigour * lerp(1, stature, 0.35);
          ratio = 1.15 + wide * 0.50;
          break;
        // flatter than it is anything else; that is what a cushion is for
        case 'cushion':
          h = (0.22 + grow * 0.30) * vigour * lerp(1, stature, 0.25);
          ratio = 1.90 + wide * 0.95;
          break;
        // all height and no spread — the one tree that reads as an exclamation
        case 'columnar':
          h = (emergent ? 24 + grow * 12 : 14 + grow * 10) * vigour * stature;
          ratio = 0.11 + wide * 0.17;
          break;
        // a palm is a coastal specialist: sand IS its soil, so it takes the
        // thin-ground penalty at less than half weight — on the beach whose
        // stature crushes everything else to scrub, a palm still stands up
        case 'palm':
          h = (6 + grow * 5) * vigour * lerp(1, stature, 0.35);
          ratio = 0.42 + wide * 0.50;
          break;
        // the tree that died standing: it keeps a trunk's height with no
        // crown's width, which is exactly what makes it read as dead
        case 'snag':
          h = (5 + grow * 7) * vigour * lerp(1, stature, 0.5);
          ratio = 0.30 + wide * 0.20;
          break;
        case 'fern':
          h = (0.5 + grow * 0.8) * vigour * lerp(1, stature, 0.4);
          ratio = 1.60 + wide * 0.80;
          break;
        case 'flower':
          h = (0.28 + grow * 0.36) * vigour * lerp(1, stature, 0.3);
          ratio = 0.90 + wide * 0.50;
          break;
        case 'willow':
          h = (9 + grow * 7) * vigour * stature;
          ratio = 0.85 + wide * 0.80;
          break;
        // --- v0.5, and every height here was chosen against the biome's own
        // median rather than against what the plant is in life. `tools/form.ts`
        // reports dry scrub at a 1.4 m mean and grassland at 2.1 m; a kind that
        // does not clear those is a column in a table (§13's marsh finding), so
        // five of the seven stand deliberately above their own country.
        //
        // a water tank on a stem: tall for the driest ground on the island, and
        // it takes the thin-soil penalty at less than a third, because drought
        // IS its home — the same argument `palm` makes about sand
        case 'succulent':
          h = (1.6 + grow * 2.6) * vigour * lerp(1, stature, 0.30);
          ratio = 0.22 + wide * 0.22;
          break;
        // bunchgrass: about twice a tuft, and narrow, so a grassland reads as
        // stippled with standing seed heads rather than as mown
        case 'plume':
          h = (1.1 + grow * 1.5) * vigour * lerp(1, stature, 0.45);
          ratio = 0.34 + wide * 0.30;
          break;
        // a broom is a shrub's height and a reed's width — that pairing is the
        // whole of why chaparral stops reading as coastal scrub
        case 'broom':
          h = (1.0 + grow * 1.6) * vigour * lerp(1, stature, 0.45);
          ratio = 0.30 + wide * 0.26;
          break;
        // The flattest PROPORTION in the kit: as drawn it is 7.6 to 11.8 times
        // as wide as it is tall, against a cushion's 6.6 to 9.9 — ground
        // texture with a plant's colour, which is exactly what a strand mat is
        // and exactly what §13 says short country has to separate by.
        //
        // Both those figures are the drawn aspect, `ratio × 2 × the build's
        // half-width ÷ the build's height`, and the two builds differ enough
        // that the nominal ratios (3.1–4.8 against 1.9–2.9) get the ORDER of
        // magnitude of the difference wrong. In absolute drawn height a
        // creeper and a cushion are the same thing: 0.10 m against 0.09 m.
        case 'creeper':
          h = (0.16 + grow * 0.20) * vigour * lerp(1, stature, 0.20);
          ratio = 3.10 + wide * 1.70;
          break;
        // A bare stalk under a flat head, standing clear of the mounds it grows
        // between — the head is the signal and the stalk is nearly invisible.
        //
        // **Raised a quarter, and the reason is the only measured failure of
        // this whole column.** The law was `0.9 + grow · 1.4`, which puts an
        // umbel's nominal median at 1.41 m against coastal scrub's 2.3 m mean
        // — comfortably "taller than its biome" by the number this file used
        // to reason with. It is not: an `umbel` build tops out at y = 0.866,
        // so 1.41 nominal is **1.22 m drawn**, against coastal scrub's 1.23 m
        // drawn median. Exactly 0.99×. The one kind aimed at the island's
        // tightest pair (0.12) was the one kind that did not clear its own
        // country, and no instrument in the repo could see it until
        // `tools/form.ts` grew its §"as drawn" table.
        //
        // ×1.26 takes it to **1.54 m drawn against coastal scrub's 1.28 m**,
        // 1.20×. Not 1.25×, and the gap between those two is worth a line:
        // umbel is 10% of coastal scrub's plants, so raising it raises the
        // median it is being compared against. A ratio like this is a fixed
        // point, not a lever, and `tools/form.ts` has to be re-run after the
        // change rather than predicted from before it.
        case 'umbel':
          h = (1.15 + grow * 1.75) * vigour * lerp(1, stature, 0.45);
          ratio = 0.30 + wide * 0.26;
          break;
        // A tree that gave up standing: wider than tall, and the only WOODY
        // crown on the island whose mass sits below a metre — 0.52 m drawn,
        // against a shrub's and a broom's 1.42 and everything else woody at
        // 3.8 m or more. (Plenty of the understory draws lower; the point is
        // that this is a conifer's silhouette down there.)
        case 'krummholz':
          h = (0.5 + grow * 1.1) * vigour * lerp(1, stature, 0.35);
          ratio = 1.60 + wide * 1.10;
          break;
        // The mast: 3.94 m drawn over ground whose own drawn median is 1.15 m
        // in chaparral and 1.04 m in dry scrub — 3.4× its country, which is
        // what makes it findable from the air — and the narrowest ratio in the
        // table. (3.9 m, not the 4 m this line used to round to, and the
        // comparison is against the biome's DRAWN median rather than the 1.4 m
        // nominal mean it used to quote; those are different numbers.)
        case 'candle':
          h = (2.6 + grow * 3.2) * vigour * lerp(1, stature, 0.25);
          ratio = 0.10 + wide * 0.11;
          break;
        default:
          h = (0.40 + grow * 0.70) * vigour * lerp(1, stature, 0.4);
          ratio = 0.62 + wide * 0.96;
      }

      let col = mixRgb(b.colour[0], b.colour[1], tone);
      col = mixRgb(col, [66, 100, 62], clamp(site.rain * 1.5 - 0.42, 0, 1) * 0.62);
      col = mixRgb(col, [52, 86, 56], wet * 0.42);
      col = mixRgb(col, [126, 120, 78], (1 - vigour) * 0.45);
      /**
       * The kind's own cast — **three targets, and which one a plant wears is
       * a hash of where it stands**.
       *
       * This was one fixed target at one fixed strength per kind, and
       * `tools/foliage.ts` section 6 priced what that cost. Between two plants
       * of one species standing within forty metres of each other, mean
       * |ΔL| was **0.0477** — varied — but mean |Δchromaticity| was **0.0100**,
       * and for palm 0.0021, columnar 0.0036, snag 0.0037, reed 0.0041,
       * willow 0.0046. Two neighbouring palms were the same green to within a
       * fifth of a percent of hue. That is the live-page reading ("essentially
       * no hue variation between individuals") turned into a signed number,
       * and the cause was structural rather than a weak constant: of the two
       * per-plant draws in this loop that touch colour, `shade` is a scalar
       * multiply and `tone` mixes two biome greens that are already
       * near-identical in hue, so BOTH move value and NEITHER moves hue.
       *
       * Those five figures are the current `tools/foliage.ts` §6 run against
       * `git show HEAD:src` — the model as actually shipped — and they are
       * stated that precisely because the first draft of this comment quoted
       * 0.0116 / 0.0022 / 0.0039 / 0.0048 / 0.0049, which is close enough to
       * look right and reproduces nowhere. **A before-figure must name the
       * revision it was taken at**, or the improvement it anchors is
       * unfalsifiable.
       *
       * `flower` was the one kind that measured wide (**0.0483** at HEAD,
       * 0.0558 now), and it is also the one kind that already chose its target
       * per instance — the mechanism was proved in this file before it was
       * generalised. Every kind now does what flower did, from a position hash
       * rather than from `tone` so no draw is inserted into the shared stream
       * (inserting one re-deals every plant after it).
       *
       * One `hash3`, four byte-slices. The slices of a hash with this
       * avalanche are independent enough for a tint and it is a quarter of the
       * cost of four `hash01` calls, which matters at ~600 plants a chunk.
       */
      const tint = hash3(Math.round(x * 5), Math.round(z * 5), region.seed + 0x36e);
      const cast = KIND_CAST[kind];
      // a snag is not foliage and must not tint like it: its three targets are
      // absolute bone-greys, mixed from each other rather than from the leaf
      // colour. The strongest cast in the table, on purpose.
      col = cast.absolute
        ? mixRgb(cast.to[(tint & 0xff) < 96 ? 0 : (tint & 0xff) < 176 ? 1 : 2],
                 cast.to[1], tone)
        : mixRgb(col, cast.to[(tint & 0xff) < 96 ? 0 : (tint & 0xff) < 176 ? 1 : 2],
                 // …and how HARD it takes the cast varies too, so the three
                 // targets are three clouds rather than three points
                 cast.t * (0.74 + ((tint >>> 8) & 0xff) / 256 * 0.52));
      /**
       * The last nudge, and the one that fills the gaps between the three: a
       * per-instance push on red and blue against green.
       *
       * It is deliberately NOT a push on all three — that is a value change,
       * and value already varies (`shade`, ±14%, measured |ΔL| 0.042). Moving
       * red and blue while green stands still rotates the colour instead, which
       * is the axis that measured dead. HUE_JITTER was swept against
       * `tools/foliage.ts`: see the constant.
       */
      col = [
        col[0] * (1 + (((tint >>> 16) & 0xff) / 256 - 0.5) * 2 * HUE_JITTER),
        col[1],
        col[2] * (1 + (((tint >>> 24) & 0xff) / 256 - 0.5) * 2 * HUE_JITTER),
      ];
      // a dead low plant: the silhouette stands, the green is gone, and it
      // settles a little — parched straw-grey, never the overlay's char
      if (dead && kind !== 'snag') {
        col = mixRgb([126, 112, 82], [94, 86, 66], tone);
        h *= 0.85;
      }

      // The rung's thinning, and it happens only after the whole rng draw, so
      // every LOD stays in step. The rank is hashed from the plant's own
      // position — never from the draw order — so a plant carries the same rank
      // on every rung and in every worker, and a coarse rung can only ever be
      // omitting members of the finer rung's set.
      const rank = hash01(Math.round(x * 8), Math.round(z * 8), region.seed + 0x51d);
      if (rank >= keepFraction(lod, h)) continue;
      // nothing terrestrial stands in a river (2026-08-19): the shared river
      // model says where the water is, and a plant rolled there is skipped —
      // after the whole draw, so every other plant in the chunk keeps its
      // numbers, and on every LOD alike. The bank keeps its biome's plants.
      { const rv = riversOf(region).at(x, z); if (rv && rv.inWater) continue; }
      out.push({
        x, z,
        // the geometry stands on its own base; sink it a hair so a plant on a
        // slope beds into the ground instead of hovering over the downhill side
        y: surfaceHeight(region, x, z, detail) - h * 0.03,
        scaleXZ: h * ratio,
        scaleY: h,
        rot,
        colour: [col[0] / 255 * shade, col[1] / 255 * shade, col[2] / 255 * shade],
        kind,
        variant: hash3(Math.round(x * 4), Math.round(z * 4), region.seed + 0x9a1)
                 % VEG_VARIANTS[kind],
      });
    }
  }
  return out;
}
